import { buildShirtCollarBand, expandDraftViewBox, recomputeShirtCollarBandGeometry } from '@/lib/patterns/shirt/collarBand';
import { buildShirtCollarLeaf, buildShirtCollarLeafOnBody } from '@/lib/patterns/shirt/collarLeaf';

export const SHIRT_V1_FORMULA_VERSION = 'shirt_siy_v1';
export const SHIRT_V2_FORMULA_VERSION = 'shirt_siy_v2';
export const SHIRT_V3_FORMULA_VERSION = 'shirt_siy_v3';
export const SHIRT_ACTIVE_FORMULA_VERSION = SHIRT_V3_FORMULA_VERSION;

const EIGHTH = 0.125;
const SIXTEENTH = 0.0625;

const n = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const roundEighth = (value) => Math.round(value / EIGHTH) * EIGHTH;
const roundSixteenth = (value) => Math.round(value / SIXTEENTH) * SIXTEENTH;
const rp = (x, y) => ({ x: roundEighth(x), y: roundEighth(y) });
const rp16 = (x, y) => ({ x: roundSixteenth(x), y: roundSixteenth(y) });
const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const toSVG = (p) => `${p.x} ${p.y}`;
const clampMin = (value, min) => (value < min ? min : value);
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const toInternalPoint = (point) => rp(point.x, -point.y);
const toInternalPointExact = (point) => ({ x: Number(point.x) || 0, y: -(Number(point.y) || 0) });

const normalizeShirtPointDeltaMap = (rawPointDeltas = {}) => Object.entries(
  rawPointDeltas && typeof rawPointDeltas === 'object' ? rawPointDeltas : {},
).reduce((acc, [pointName, delta]) => {
  if (!pointName || !delta || typeof delta !== 'object') return acc;
  const x = roundSixteenth(delta.x);
  const y = roundSixteenth(delta.y);
  if (Math.abs(x) < 1e-8 && Math.abs(y) < 1e-8) return acc;
  acc[pointName] = { x, y };
  return acc;
}, {});

const normalizeVector = (vector) => {
  const length = Math.hypot(vector.x, vector.y);
  if (length < 1e-6) return { x: 1, y: 0 };
  return { x: vector.x / length, y: vector.y / length };
};

const angleFromHorizontalDegrees = (startPoint, endPoint) => {
  const dx = Math.abs((endPoint?.x || 0) - (startPoint?.x || 0));
  const dy = Math.abs((endPoint?.y || 0) - (startPoint?.y || 0));
  if (dx < 1e-8 && dy < 1e-8) return 0;
  if (dx < 1e-8) return 90;
  return (Math.atan2(dy, dx) * 180) / Math.PI;
};

function quadLength(p0, p1, p2, steps = 28) {
  let total = 0;
  let prev = p0;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const inv = 1 - t;
    const next = {
      x: inv * inv * p0.x + 2 * inv * t * p1.x + t * t * p2.x,
      y: inv * inv * p0.y + 2 * inv * t * p1.y + t * t * p2.y,
    };
    total += dist(prev, next);
    prev = next;
  }

  return total;
}

function quadPoint(p0, p1, p2, t) {
  const inv = 1 - t;
  return {
    x: inv * inv * p0.x + 2 * inv * t * p1.x + t * t * p2.x,
    y: inv * inv * p0.y + 2 * inv * t * p1.y + t * t * p2.y,
  };
}

function quadSplitAtT(p0, p1, p2, tRaw) {
  const t = clamp(tRaw, 0, 1);
  const a = {
    x: p0.x + (p1.x - p0.x) * t,
    y: p0.y + (p1.y - p0.y) * t,
  };
  const b = {
    x: p1.x + (p2.x - p1.x) * t,
    y: p1.y + (p2.y - p1.y) * t,
  };
  const m = {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  };

  return {
    splitPoint: m,
    left: { p0, p1: a, p2: m },
    right: { p0: m, p1: b, p2 },
  };
}

function solveQuadTForArcDistanceFromStart(p0, p1, p2, targetDistance) {
  const total = quadLength(p0, p1, p2, 56);
  const clamped = clamp(targetDistance, 0, total);

  if (clamped <= 1e-6) return 0;
  if (Math.abs(clamped - total) <= 1e-6) return 1;

  let lo = 0;
  let hi = 1;

  for (let i = 0; i < 28; i += 1) {
    const mid = (lo + hi) / 2;
    const split = quadSplitAtT(p0, p1, p2, mid);
    const len = quadLength(split.left.p0, split.left.p1, split.left.p2, 42);
    if (len < clamped) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return (lo + hi) / 2;
}

function splitQuadraticAtDistanceFromStart(p0, p1, p2, distanceFromStart) {
  const t = solveQuadTForArcDistanceFromStart(p0, p1, p2, distanceFromStart);
  return quadSplitAtT(p0, p1, p2, t);
}

function splitQuadraticAtDistanceFromEnd(p0, p1, p2, distanceFromEnd) {
  const total = quadLength(p0, p1, p2, 56);
  const fromStart = clamp(total - distanceFromEnd, 0, total);
  return splitQuadraticAtDistanceFromStart(p0, p1, p2, fromStart);
}

function quadSegmentToPathCommand(segment) {
  return `Q ${toSVG(segment.p1)} ${toSVG(segment.p2)}`;
}

function quadSegmentsToPathCommands(segments = []) {
  return segments.map((segment) => quadSegmentToPathCommand(segment)).join('\n    ');
}

function cubicLength(p0, p1, p2, p3, steps = 56) {
  const points = sampleCubic(p0, p1, p2, p3, steps);
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += dist(points[i - 1], points[i]);
  }
  return total;
}

function cubicSplitAtT(p0, p1, p2, p3, t) {
  const p01 = rp16(p0.x + ((p1.x - p0.x) * t), p0.y + ((p1.y - p0.y) * t));
  const p12 = rp16(p1.x + ((p2.x - p1.x) * t), p1.y + ((p2.y - p1.y) * t));
  const p23 = rp16(p2.x + ((p3.x - p2.x) * t), p2.y + ((p3.y - p2.y) * t));
  const p012 = rp16(p01.x + ((p12.x - p01.x) * t), p01.y + ((p12.y - p01.y) * t));
  const p123 = rp16(p12.x + ((p23.x - p12.x) * t), p12.y + ((p23.y - p12.y) * t));
  const p0123 = rp16(p012.x + ((p123.x - p012.x) * t), p012.y + ((p123.y - p012.y) * t));

  return {
    left: { type: 'C', p0, p1: p01, p2: p012, p3: p0123 },
    right: { type: 'C', p0: p0123, p1: p123, p2: p23, p3 },
    splitPoint: p0123,
  };
}

function solveCubicTForArcDistanceFromStart(p0, p1, p2, p3, distanceFromStart) {
  const total = cubicLength(p0, p1, p2, p3, 84);
  const clamped = clamp(distanceFromStart, 0, total);

  if (clamped <= 1e-6) return 0;
  if (Math.abs(clamped - total) <= 1e-6) return 1;

  let lo = 0;
  let hi = 1;

  for (let i = 0; i < 28; i += 1) {
    const mid = (lo + hi) / 2;
    const split = cubicSplitAtT(p0, p1, p2, p3, mid);
    const len = cubicLength(split.left.p0, split.left.p1, split.left.p2, split.left.p3, 48);
    if (len < clamped) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return (lo + hi) / 2;
}

function splitCubicAtDistanceFromStart(p0, p1, p2, p3, distanceFromStart) {
  const t = solveCubicTForArcDistanceFromStart(p0, p1, p2, p3, distanceFromStart);
  return cubicSplitAtT(p0, p1, p2, p3, t);
}

function splitCubicAtDistanceFromEnd(p0, p1, p2, p3, distanceFromEnd) {
  const total = cubicLength(p0, p1, p2, p3, 84);
  const fromStart = clamp(total - distanceFromEnd, 0, total);
  return splitCubicAtDistanceFromStart(p0, p1, p2, p3, fromStart);
}

function segmentToPathCommand(segment) {
  if (!segment) return '';
  if (segment.type === 'C' || segment.p3) {
    return `C ${toSVG(segment.p1)} ${toSVG(segment.p2)} ${toSVG(segment.p3)}`;
  }
  return `Q ${toSVG(segment.p1)} ${toSVG(segment.p2)}`;
}

function segmentsToPathCommands(segments = []) {
  return segments.map((segment) => segmentToPathCommand(segment)).join('\n    ');
}

function quadToCubicControls(p0, control, p2) {
  return {
    c1: rp16(
      p0.x + ((2 / 3) * (control.x - p0.x)),
      p0.y + ((2 / 3) * (control.y - p0.y)),
    ),
    c2: rp16(
      p2.x + ((2 / 3) * (control.x - p2.x)),
      p2.y + ((2 / 3) * (control.y - p2.y)),
    ),
  };
}

function cubicPoint(p0, p1, p2, p3, t) {
  const inv = 1 - t;
  return {
    x: inv ** 3 * p0.x
      + 3 * inv ** 2 * t * p1.x
      + 3 * inv * t ** 2 * p2.x
      + t ** 3 * p3.x,
    y: inv ** 3 * p0.y
      + 3 * inv ** 2 * t * p1.y
      + 3 * inv * t ** 2 * p2.y
      + t ** 3 * p3.y,
  };
}

function sampleQuadratic(p0, p1, p2, steps = 72) {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    points.push(quadPoint(p0, p1, p2, i / steps));
  }
  return points;
}

function sampleCubic(p0, p1, p2, p3, steps = 72) {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    points.push(cubicPoint(p0, p1, p2, p3, i / steps));
  }
  return points;
}

function polylineLength(points = []) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += dist(points[i - 1], points[i]);
  }
  return total;
}

function splitPolylineAtDistance(points = [], targetDistance = 0) {
  if (!points || points.length === 0) {
    return { before: [], after: [] };
  }

  if (points.length === 1) {
    return { before: [points[0]], after: [points[0]] };
  }

  const total = polylineLength(points);
  const clamped = clamp(targetDistance, 0, total);

  if (clamped <= 1e-6) {
    return {
      before: [points[0]],
      after: [...points],
    };
  }

  if (Math.abs(clamped - total) <= 1e-6) {
    return {
      before: [...points],
      after: [points[points.length - 1]],
    };
  }

  const before = [points[0]];
  let walked = 0;

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const seg = dist(prev, curr);
    if (walked + seg >= clamped) {
      const remain = clamped - walked;
      const t = seg > 1e-9 ? remain / seg : 0;
      const splitPoint = {
        x: prev.x + (curr.x - prev.x) * t,
        y: prev.y + (curr.y - prev.y) * t,
      };

      before.push(splitPoint);
      return {
        before,
        after: [splitPoint, ...points.slice(i)],
      };
    }

    before.push(curr);
    walked += seg;
  }

  return {
    before: [...points],
    after: [points[points.length - 1]],
  };
}

function splitPolylineAtY(points = [], targetY = 0) {
  if (!points || points.length < 2) {
    return { upper: [...points], lower: [...points], splitPoint: points[0] || { x: 0, y: targetY } };
  }

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const prevDelta = prev.y - targetY;
    const currDelta = curr.y - targetY;

    if (Math.abs(prevDelta) <= 1e-9) {
      return {
        upper: points.slice(0, i),
        lower: points.slice(i - 1),
        splitPoint: prev,
      };
    }

    if ((prevDelta < 0 && currDelta > 0) || (prevDelta > 0 && currDelta < 0) || Math.abs(currDelta) <= 1e-9) {
      const segDy = curr.y - prev.y;
      const t = Math.abs(segDy) > 1e-9 ? (targetY - prev.y) / segDy : 0;
      const splitPoint = {
        x: prev.x + (curr.x - prev.x) * t,
        y: targetY,
      };

      return {
        upper: [...points.slice(0, i), splitPoint],
        lower: [splitPoint, ...points.slice(i)],
        splitPoint,
      };
    }
  }

  const midDistance = polylineLength(points) * 0.5;
  const fallback = splitPolylineAtDistance(points, midDistance);
  const splitPoint = fallback.before[fallback.before.length - 1] || points[0];
  return {
    upper: fallback.before,
    lower: fallback.after,
    splitPoint,
  };
}

function boundsFromPoints(points = []) {
  if (!points.length) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }

  return {
    minX: Math.min(...points.map((point) => point.x)),
    minY: Math.min(...points.map((point) => point.y)),
    maxX: Math.max(...points.map((point) => point.x)),
    maxY: Math.max(...points.map((point) => point.y)),
  };
}

function translatePoints(points = [], dx = 0, dy = 0) {
  return points.map((point) => ({
    x: point.x + dx,
    y: point.y + dy,
  }));
}

function toLineCommands(points = []) {
  if (!points || points.length < 2) return '';
  return points
    .slice(1)
    .map((point) => `L ${toSVG(point)}`)
    .join('\n    ');
}

function getPointOnPolyline(points = [], atDistance = 0) {
  const split = splitPolylineAtDistance(points, atDistance);
  return split.before[split.before.length - 1] || points[0] || { x: 0, y: 0 };
}

function normalizeOrFallback(vector, fallback = { x: -1, y: 0 }) {
  const length = Math.hypot(vector.x, vector.y);
  if (length < 1e-6) return fallback;
  return {
    x: vector.x / length,
    y: vector.y / length,
  };
}

function smoothQuadraticTurnPoint({
  prevPoint,
  turnPoint,
  nextPoint,
  incomingControl,
  outgoingControl,
}) {
  const incomingLength = Math.max(0.125, dist(turnPoint, incomingControl));
  const outgoingLength = Math.max(0.125, dist(turnPoint, outgoingControl));

  const incomingTangent = normalizeOrFallback({
    x: turnPoint.x - incomingControl.x,
    y: turnPoint.y - incomingControl.y,
  }, normalizeOrFallback({
    x: turnPoint.x - prevPoint.x,
    y: turnPoint.y - prevPoint.y,
  }, { x: 0, y: 1 }));

  const outgoingTangent = normalizeOrFallback({
    x: outgoingControl.x - turnPoint.x,
    y: outgoingControl.y - turnPoint.y,
  }, normalizeOrFallback({
    x: nextPoint.x - turnPoint.x,
    y: nextPoint.y - turnPoint.y,
  }, { x: 0, y: 1 }));

  const blendedTangent = normalizeOrFallback({
    x: incomingTangent.x + outgoingTangent.x,
    y: incomingTangent.y + outgoingTangent.y,
  }, normalizeOrFallback({
    x: nextPoint.x - prevPoint.x,
    y: nextPoint.y - prevPoint.y,
  }, { x: 0, y: 1 }));

  return {
    incomingControl: rp16(
      turnPoint.x - blendedTangent.x * incomingLength,
      turnPoint.y - blendedTangent.y * incomingLength,
    ),
    outgoingControl: rp16(
      turnPoint.x + blendedTangent.x * outgoingLength,
      turnPoint.y + blendedTangent.y * outgoingLength,
    ),
  };
}

function buildQuadraticBridgeControl({
  startPoint,
  endPoint,
  startTangent,
  endTangent,
}) {
  const t0 = normalizeOrFallback(startTangent, { x: 1, y: 0 });
  const t1 = normalizeOrFallback(endTangent, { x: 1, y: 0 });

  const a1 = startPoint;
  const a2 = { x: startPoint.x + t0.x, y: startPoint.y + t0.y };
  const b1 = endPoint;
  const b2 = { x: endPoint.x - t1.x, y: endPoint.y - t1.y };

  const det = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (Math.abs(det) < 1e-6) {
    return rp16(
      (startPoint.x + endPoint.x) / 2,
      (startPoint.y + endPoint.y) / 2,
    );
  }

  const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / det;
  return rp16(
    a1.x + (a2.x - a1.x) * t,
    a1.y + (a2.y - a1.y) * t,
  );
}

function buildShoulderJoinCommand({
  startPoint,
  endPoint,
  startSmooth,
  endSmooth,
  startTangent,
  endTangent,
}) {
  if (!startSmooth && !endSmooth) {
    return `L ${toSVG(endPoint)}`;
  }

  const span = Math.max(0.25, dist(startPoint, endPoint));
  const handleLength = clamp(span * 0.5, 0.25, 4);

  let control;
  if (startSmooth && endSmooth) {
    control = buildQuadraticBridgeControl({
      startPoint,
      endPoint,
      startTangent,
      endTangent,
    });
  } else if (startSmooth) {
    const t0 = normalizeOrFallback(startTangent, { x: 1, y: 0 });
    control = rp16(
      startPoint.x + t0.x * handleLength,
      startPoint.y + t0.y * handleLength,
    );
  } else {
    const t1 = normalizeOrFallback(endTangent, { x: 1, y: 0 });
    control = rp16(
      endPoint.x - t1.x * handleLength,
      endPoint.y - t1.y * handleLength,
    );
  }

  return `Q ${toSVG(control)} ${toSVG(endPoint)}`;
}

function rotateAroundAnchor(point, anchor, angleRadians) {
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);

  return {
    x: anchor.x + dx * cos - dy * sin,
    y: anchor.y + dx * sin + dy * cos,
  };
}

function reflectPointAcrossLine(point, lineA, lineB) {
  const dx = lineB.x - lineA.x;
  const dy = lineB.y - lineA.y;
  const lenSq = dx * dx + dy * dy;

  if (lenSq <= 1e-9) {
    return rp16(point.x, point.y);
  }

  const t = ((point.x - lineA.x) * dx + (point.y - lineA.y) * dy) / lenSq;
  const proj = {
    x: lineA.x + dx * t,
    y: lineA.y + dy * t,
  };

  return rp16(
    proj.x * 2 - point.x,
    proj.y * 2 - point.y,
  );
}

function polylineSegmentLength(points = []) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += dist(points[i - 1], points[i]);
  }
  return total;
}

function getPointAndTangentOnPolyline(points = [], atDistance = 0) {
  if (!points || points.length < 2) {
    return {
      point: points[0] || { x: 0, y: 0 },
      tangent: { x: 1, y: 0 },
    };
  }

  const total = polylineLength(points);
  const clamped = clamp(atDistance, 0, total);

  if (clamped <= 1e-9) {
    return {
      point: points[0],
      tangent: normalizeOrFallback({
        x: points[1].x - points[0].x,
        y: points[1].y - points[0].y,
      }),
    };
  }

  let walked = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const segLen = dist(prev, curr);

    if (walked + segLen >= clamped) {
      const t = segLen > 1e-9 ? (clamped - walked) / segLen : 0;
      return {
        point: {
          x: prev.x + (curr.x - prev.x) * t,
          y: prev.y + (curr.y - prev.y) * t,
        },
        tangent: normalizeOrFallback({
          x: curr.x - prev.x,
          y: curr.y - prev.y,
        }),
      };
    }

    walked += segLen;
  }

  const last = points[points.length - 1];
  const beforeLast = points[points.length - 2];
  return {
    point: last,
    tangent: normalizeOrFallback({
      x: last.x - beforeLast.x,
      y: last.y - beforeLast.y,
    }),
  };
}

function buildNotchSymbolPaths(point, tangent, type = 'single_t', options = {}) {
  const {
    size = 0.2,
    depth = 0.28,
    inwardToward = null,
  } = options;

  const t = normalizeOrFallback(tangent, { x: 1, y: 0 });
  let nrm = normalizeOrFallback({ x: -t.y, y: t.x }, { x: 0, y: -1 });

  if (inwardToward) {
    const inwardVector = {
      x: inwardToward.x - point.x,
      y: inwardToward.y - point.y,
    };
    const align = nrm.x * inwardVector.x + nrm.y * inwardVector.y;
    if (align < 0) {
      nrm = { x: -nrm.x, y: -nrm.y };
    }
  }

  const makeT = (base) => {
    const tip = {
      x: base.x + nrm.x * depth,
      y: base.y + nrm.y * depth,
    };
    const barHalf = size * 0.5;
    const barA = {
      x: tip.x - t.x * barHalf,
      y: tip.y - t.y * barHalf,
    };
    const barB = {
      x: tip.x + t.x * barHalf,
      y: tip.y + t.y * barHalf,
    };

    return `M ${toSVG(base)} L ${toSVG(tip)} M ${toSVG(barA)} L ${toSVG(barB)}`;
  };

  if (type === 'single_v') {
    const apex = {
      x: point.x + nrm.x * depth,
      y: point.y + nrm.y * depth,
    };
    const armHalf = size * 0.52;
    const left = {
      x: apex.x - t.x * armHalf,
      y: apex.y - t.y * armHalf,
    };
    const right = {
      x: apex.x + t.x * armHalf,
      y: apex.y + t.y * armHalf,
    };

    return [`M ${toSVG(point)} L ${toSVG(left)} M ${toSVG(point)} L ${toSVG(right)}`];
  }

  if (type === 'double_t') {
    const shift = size * 0.56;
    const baseA = {
      x: point.x - t.x * shift,
      y: point.y - t.y * shift,
    };
    const baseB = {
      x: point.x + t.x * shift,
      y: point.y + t.y * shift,
    };
    return [makeT(baseA), makeT(baseB)];
  }

  return [makeT(point)];
}

const dot = (a, b) => (a.x * b.x) + (a.y * b.y);

function toNamedPoints(piece, points) {
  return Object.entries(points).map(([name, point]) => ({
    name,
    piece,
    x: toInternalPointExact(point).x,
    y: toInternalPointExact(point).y,
  }));
}

function createViewBox(points = []) {
  if (!points.length) {
    return { x: -4, y: -36, width: 28, height: 42 };
  }

  const minX = Math.min(...points.map((p) => p.x));
  const maxX = Math.max(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxY = Math.max(...points.map((p) => p.y));
  const pad = 2.5;

  return {
    x: roundEighth(minX - pad),
    y: roundEighth(-maxY - pad),
    width: roundEighth(maxX - minX + pad * 2),
    height: roundEighth(maxY - minY + pad * 2),
  };
}

function adjustPairSimultaneously(frontBase, backBase, intake, targetHalf, minWidth = 0.75) {
  const baseTotalHalf = roundEighth(frontBase + backBase - intake);
  const deltaHalf = targetHalf - baseTotalHalf;
  const stepCount = Math.round(deltaHalf / (EIGHTH * 2));

  let front = roundEighth(frontBase + stepCount * EIGHTH);
  let back = roundEighth(backBase + stepCount * EIGHTH);

  if (front < minWidth || back < minWidth) {
    const limitedStep = Math.max(
      Math.ceil((minWidth - roundEighth(frontBase)) / EIGHTH),
      Math.ceil((minWidth - roundEighth(backBase)) / EIGHTH),
    );
    front = roundEighth(frontBase + limitedStep * EIGHTH);
    back = roundEighth(backBase + limitedStep * EIGHTH);
  }

  return {
    front,
    back,
    steps: roundEighth((front - roundEighth(frontBase)) / EIGHTH),
    totalHalf: roundEighth(front + back - intake),
  };
}

function resolveFrontBackSplitByFront({
  targetHalf,
  preferredFront,
  minWidth = 0.75,
}) {
  const total = roundEighth(Math.max(minWidth * 2, targetHalf));
  const rawFront = Number.isFinite(preferredFront) ? roundEighth(preferredFront) : roundEighth(total / 2);
  const clampedFront = roundEighth(clamp(rawFront, minWidth, total - minWidth));
  const back = roundEighth(total - clampedFront);

  return {
    front: clampedFront,
    back,
    total,
  };
}

function buildNeckCurves({
  neck,
  xCF,
  backToFtHps,
  frontNeckWidth,
  backNeckWidth,
  backNeckDepth,
  frontNeckDepth,
  useFrontDepthOverride,
}) {
  const CB1 = rp(0, backNeckDepth);
  const BHPS = rp(backNeckWidth, 0);
  const CF0 = rp(xCF, backToFtHps);
  const FHPS = rp(xCF - frontNeckWidth, CF0.y);

  const backCtrl = rp(
    backNeckWidth * 0.64,
    CB1.y,
  );
  const backNeckLen = quadLength(CB1, backCtrl, BHPS);

  const targetHalfNeckMin = neck / 2;
  const targetHalfNeckMax = targetHalfNeckMin + 0.125;

  let frontDepth = frontNeckDepth;
  let step = 0.125;
  let frontState = null;

  const maxIterations = useFrontDepthOverride ? 1 : 80;

  for (let i = 0; i < maxIterations; i += 1) {
    const CF1 = rp(xCF, CF0.y + frontDepth);
    const frontCtrl = rp(
      xCF - frontNeckWidth * 0.82,
      CF1.y,
    );
    const frontNeckLen = quadLength(CF1, frontCtrl, FHPS);
    const totalHalf = backNeckLen + frontNeckLen;

    frontState = {
      CF1,
      frontCtrl,
      totalHalf,
      frontNeckLen,
    };

    if (useFrontDepthOverride) {
      break;
    }

    if (totalHalf >= targetHalfNeckMin && totalHalf <= targetHalfNeckMax) {
      break;
    }

    if (totalHalf < targetHalfNeckMin) {
      frontDepth += step;
    } else {
      frontDepth = Math.max(0.375, frontDepth - step);
    }

    if (i % 8 === 7) {
      step = Math.max(0.015625, step / 2);
    }
  }

  return {
    CB1,
    BHPS,
    CF0,
    FHPS,
    CF1: frontState.CF1,
    backCtrl,
    frontCtrl: frontState.frontCtrl,
    backNeckLen,
    frontNeckLen: frontState.frontNeckLen,
    halfNeckLength: frontState.totalHalf,
    resolvedFrontNeckDepth: frontState.CF1.y - CF0.y,
    targetHalfNeckMin,
    targetHalfNeckMax,
  };
}

function generateShirtPatternBase(measurements, controls = {}, versionConfig = {}) {
  const {
    formulaVersion = SHIRT_V1_FORMULA_VERSION,
    defaultLastButtonPosition = 3.5,
    preserveArmPart2CtrlYOnXDrag = false,
    decoupleArmholeSplitControls = false,
    applyNetCompensatedDartSplit = false,
  } = versionConfig;
  const resolvedFormulaVersion = controls?.formulaVersion || formulaVersion;
  const freeModeActive = Boolean(controls?.freeModeActive);
  const freePointDeltas = freeModeActive
    ? normalizeShirtPointDeltaMap(controls?.pointDeltas || {})
    : {};
  const applyPointDeltaInPlace = (pointName, point) => {
    const delta = freePointDeltas[pointName];
    if (!delta || !point) return point;
    point.x = (Number(point.x) || 0) + delta.x;
    point.y = (Number(point.y) || 0) + delta.y;
    return point;
  };
  const neck = n(measurements.neck, 15);
  const chest = n(measurements.chest, 40);
  const waist = n(measurements.waist, 36);
  const belly = n(measurements.belly, waist + 2);
  const seat = n(measurements.seat, chest);
  const portlyTypeRaw = typeof measurements.portlyType === 'string' ? measurements.portlyType : 'normal';
  const portlyType = ['thin', 'normal', 'slight_belly', 'big_belly'].includes(portlyTypeRaw)
    ? portlyTypeRaw
    : 'normal';
  const shoulder = n(measurements.shoulder, 18);

  const backShoulderSlope = n(measurements.backShoulderSlope, 1);
  const frontShoulderSlope = n(measurements.frontShoulderSlope, 1.125);
  const autoBackNeckDepth = roundEighth(n(measurements.backNeckDepth, 0.75));
  const backToFtHps = n(measurements.backToFtHps, 1);
  const armholeScyeDepth = n(measurements.armholeScyeDepth, 8.25);
  const acrossBack = n(measurements.acrossBack, 15);
  const acrossFront = n(measurements.acrossFront, 14);
  const xfDistanceFromChest = n(measurements.xfDistanceFromChest, 2.5);

  const distanceToWaist = n(measurements.distanceToWaist, 17.5);
  const distanceToBelly = n(measurements.distanceToBelly, distanceToWaist + 4);
  const shirtLengthBack = n(measurements.shirtLengthBack, 31);
  const legacyTailDepth = n(measurements.tailDepth, 2);
  const tailDepthBack = clampMin(n(measurements.tailDepthBack, legacyTailDepth || 2), 0);
  const tailDepthFront = clampMin(n(measurements.tailDepthFront, legacyTailDepth || 2), 0);
  const hemCurveMode = n(measurements.hemCurveMode, 1);

  const fishDartEnabled = !(measurements.fishDartEnabled === false || measurements.fishDartEnabled === 0 || measurements.fishDartEnabled === '0');
  const dartWidthWaistInput = n(measurements.dartWidthWaist, 1);
  const dartWidthBellyInput = n(measurements.dartWidthBelly, 0.5);
  const dartLengthTop = n(measurements.dartLengthTop, 5);
  const dartLengthBottom = n(measurements.dartLengthBottom, 4);
  const dartWidthWaist = fishDartEnabled ? dartWidthWaistInput : 0;
  const dartWidthBelly = fishDartEnabled ? dartWidthBellyInput : 0;

  const backStyleRaw = typeof measurements.backStyle === 'string' ? measurements.backStyle : 'none';
  const backStyle = ['none', 'side_pleat', 'center_pleat'].includes(backStyleRaw) ? backStyleRaw : 'none';
  const backPleatWidth = clampMin(n(measurements.backPleatWidth, 0.75), 0);
  const backPleatLength = clampMin(n(measurements.backPleatLength, 3), 0);
  const backSidePleatOffsetFromBYDBInput = clampMin(n(measurements.backSidePleatOffsetFromBYDB, 0), 0);
  const yokeDepth = clampMin(n(measurements.yokeDepth, 3.5), 0.5);
  const yokeEnabled = !(measurements.yokeEnabled === false || measurements.yokeEnabled === 0 || measurements.yokeEnabled === '0');
  const frontNeckRollover = clampMin(n(measurements.frontNeckRollover, 1.25), 0);
  const frontArmholeRollover = clampMin(n(measurements.frontArmholeRollover, 1.75), 0);
  const buttonExtensionWidth = clampMin(n(measurements.buttonExtensionWidth, 0.625), 0);
  const placketFoldWidth = clampMin(n(measurements.placketFoldWidth, 1.25), 0);
  const manualButtonCount = Math.max(0, Math.round(n(measurements.buttonCount, 0)));
  const firstButtonPosition = clampMin(n(measurements.firstButtonPosition, 2), 0);
  const lastButtonPosition = clampMin(n(measurements.lastButtonPosition, defaultLastButtonPosition), 0);
  const yokeDartWidth = clampMin(n(measurements.yokeDartWidth, 0.75), 0);
  const yokeDartLength = clampMin(n(measurements.yokeDartLength, 4), 0);
  const yokeDartYokeCtrlDx = n(measurements.yokeDartYokeCtrlDx, 0);
  const yokeDartYokeCtrlDy = n(measurements.yokeDartYokeCtrlDy, 0.125);
  const yokeDartBackCtrlDx = n(measurements.yokeDartBackCtrlDx, 0);
  const yokeDartBackCtrlDy = n(measurements.yokeDartBackCtrlDy, 0);
  const backYokeArmSplitCtrlDx = n(measurements.backYokeArmSplitCtrlDx, 0);
  const backYokeArmSplitCtrlDy = n(measurements.backYokeArmSplitCtrlDy, 0);
  const backPanelYokeSplitCtrlDx = n(measurements.backPanelYokeSplitCtrlDx, 0);
  const backPanelYokeSplitCtrlDy = n(measurements.backPanelYokeSplitCtrlDy, 0);
  const backNeckCtrlDx = n(measurements.backNeckCtrlDx, 0);
  const backNeckCtrlDy = n(measurements.backNeckCtrlDy, 0);
  const frontNeckCtrlDx = n(measurements.frontNeckCtrlDx, 0);
  const frontNeckCtrlDy = n(measurements.frontNeckCtrlDy, 0);
  const backArmPart1CtrlDx = n(measurements.backArmPart1CtrlDx, 0);
  const backArmPart1CtrlDy = n(measurements.backArmPart1CtrlDy, 0);
  const backArmPart2CtrlDx = n(measurements.backArmPart2CtrlDx, 0);
  const backArmPart2CtrlDy = n(measurements.backArmPart2CtrlDy, 0);
  const frontArmPart1CtrlDx = n(measurements.frontArmPart1CtrlDx, 0);
  const frontArmPart1CtrlDy = n(measurements.frontArmPart1CtrlDy, 0);
  const frontArmPart2CtrlDx = n(measurements.frontArmPart2CtrlDx, 0);
  const frontArmPart2CtrlDy = n(measurements.frontArmPart2CtrlDy, 0);
  const backHemCtrl1Dx = n(measurements.backHemCtrl1Dx, 0);
  const backHemCtrl1Dy = n(measurements.backHemCtrl1Dy, 0);
  const backHemCtrl2Dx = n(measurements.backHemCtrl2Dx, 0);
  const backHemCtrl2Dy = n(measurements.backHemCtrl2Dy, 0);
  const frontHemCtrl1Dx = n(measurements.frontHemCtrl1Dx, 0);
  const frontHemCtrl1Dy = n(measurements.frontHemCtrl1Dy, 0);
  const frontHemCtrl2Dx = n(measurements.frontHemCtrl2Dx, 0);
  const frontHemCtrl2Dy = n(measurements.frontHemCtrl2Dy, 0);
  const frontChestWaistCtrlDx = n(measurements.frontChestWaistCtrlDx, 0);
  const frontChestWaistCtrlDy = n(measurements.frontChestWaistCtrlDy, 0);
  const frontWaistBellyCtrlDx = n(measurements.frontWaistBellyCtrlDx, 0);
  const frontWaistBellyCtrlDy = n(measurements.frontWaistBellyCtrlDy, 0);
  const frontBellySeatCtrlDx = n(measurements.frontBellySeatCtrlDx, 0);
  const frontBellySeatCtrlDy = n(measurements.frontBellySeatCtrlDy, 0);
  const backChestWaistCtrlDx = n(measurements.backChestWaistCtrlDx, 0);
  const backChestWaistCtrlDy = n(measurements.backChestWaistCtrlDy, 0);
  const backWaistBellyCtrlDx = n(measurements.backWaistBellyCtrlDx, 0);
  const backWaistBellyCtrlDy = n(measurements.backWaistBellyCtrlDy, 0);
  const backBellySeatCtrlDx = n(measurements.backBellySeatCtrlDx, 0);
  const backBellySeatCtrlDy = n(measurements.backBellySeatCtrlDy, 0);

  const bicepWidthInput = clampMin(n(measurements.bicepWidth, 15.5), 4);
  const sleeveStyle = measurements?.sleeveStyle === 'short' ? 'short' : 'long';
  const isShortSleeve = sleeveStyle === 'short';
  const longSleeveLengthInput = clampMin(n(measurements.sleeveLength, 26), 6);
  const longSleeveCuffWidthInput = clampMin(n(measurements.maxCuffWidth, 11), 2.5);
  const shortSleeveLengthInput = clampMin(n(measurements.shortSleeveLength, 10), 4);
  const shortSleeveHemInput = clampMin(n(measurements.shortSleeveHem, 11), 2.5);
  const elbowWidthInput = isShortSleeve
    ? roundEighth(Math.max(3.5, (bicepWidthInput * 0.55) + (shortSleeveHemInput * 0.45)))
    : clampMin(n(measurements.elbowWidth, Math.max(4, bicepWidthInput - 2)), 3.5);
  const maxCuffWidthInput = isShortSleeve
    ? shortSleeveHemInput
    : clampMin(longSleeveCuffWidthInput, 2.5);

  const legacySleeveClosedInputMode = !isShortSleeve && bicepWidthInput <= 10 && elbowWidthInput <= 9 && maxCuffWidthInput <= 8;

  const bicepWidth = legacySleeveClosedInputMode ? bicepWidthInput * 2 : bicepWidthInput;
  const elbowWidth = legacySleeveClosedInputMode ? elbowWidthInput * 2 : elbowWidthInput;
  const maxCuffWidth = legacySleeveClosedInputMode ? maxCuffWidthInput * 2 : maxCuffWidthInput;
  const sleeveLength = isShortSleeve ? shortSleeveLengthInput : longSleeveLengthInput;
  const sleeveDistToElbowInput = n(measurements.sleeveDistToElbow, 0);
  const hasSleeveDistToElbowOverride = !isShortSleeve && sleeveDistToElbowInput > 0;
  const sleeveFrontPitchPercent = clamp(n(measurements.sleeveFrontPitchPercent, 0.55), 0.2, 0.6);
  const sleeveBackPitchPercent = clamp(n(measurements.sleeveBackPitchPercent, 0.45), 0.3, 0.75);

  const sleeveFrontUnderarmCtrlDx = n(measurements.sleeveFrontUnderarmCtrlDx, 0);
  const sleeveFrontUnderarmCtrlDy = n(measurements.sleeveFrontUnderarmCtrlDy, 0);
  const sleeveFrontCrownCtrlDx = n(measurements.sleeveFrontCrownCtrlDx, 0);
  const sleeveFrontCrownCtrlDy = n(measurements.sleeveFrontCrownCtrlDy, 0);
  const sleeveBackCrownCtrlDx = n(measurements.sleeveBackCrownCtrlDx, 0);
  const sleeveBackCrownCtrlDy = n(measurements.sleeveBackCrownCtrlDy, 0);
  const sleeveBackUnderarmCtrlDx = n(measurements.sleeveBackUnderarmCtrlDx, 0);
  const sleeveBackUnderarmCtrlDy = n(measurements.sleeveBackUnderarmCtrlDy, 0);
  const sleeveFrontSideUpperCtrlDx = n(measurements.sleeveFrontSideUpperCtrlDx, 0);
  const sleeveFrontSideUpperCtrlDy = n(measurements.sleeveFrontSideUpperCtrlDy, 0);
  const sleeveFrontSideLowerCtrlDx = n(measurements.sleeveFrontSideLowerCtrlDx, 0);
  const sleeveFrontSideLowerCtrlDy = n(measurements.sleeveFrontSideLowerCtrlDy, 0);
  const sleeveBackSideUpperCtrlDx = n(measurements.sleeveBackSideUpperCtrlDx, 0);
  const sleeveBackSideUpperCtrlDy = n(measurements.sleeveBackSideUpperCtrlDy, 0);
  const sleeveBackSideLowerCtrlDx = n(measurements.sleeveBackSideLowerCtrlDx, 0);
  const sleeveBackSideLowerCtrlDy = n(measurements.sleeveBackSideLowerCtrlDy, 0);

  const sleeveCuffHeight = clampMin(n(measurements.cuffWidth, 2.5), 0);
  const sleevePleatAllowance = 0.75;
  const sleevePlacketHeight = 4;

  const overrideFNWEnabled = Boolean(measurements.overrideFNWEnabled);
  const overrideBNWEnabled = Boolean(measurements.overrideBNWEnabled);
  const overrideFNDEnabled = Boolean(measurements.overrideFNDEnabled);
  const overrideBNDEnabled = Boolean(measurements.overrideBNDEnabled);
  const overrideAcrossBackDepthEnabled = Boolean(measurements.overrideAcrossBackDepthEnabled);
  const overrideAcrossFrontDepthEnabled = Boolean(measurements.overrideAcrossFrontDepthEnabled);

  const autoNeckWidth = roundEighth(neck / 5.75);
  const autoFrontNeckWidth = autoNeckWidth;
  const autoBackNeckWidth = autoNeckWidth;
  const autoFrontNeckDepth = roundEighth(autoFrontNeckWidth + 0.5);

  const resolvedFrontNeckWidth = roundEighth(clampMin(
    overrideFNWEnabled ? n(measurements.overrideFNW, autoFrontNeckWidth) : autoFrontNeckWidth,
    0.5,
  ));

  const resolvedBackNeckWidth = roundEighth(clampMin(
    overrideBNWEnabled ? n(measurements.overrideBNW, autoBackNeckWidth) : autoBackNeckWidth,
    0.5,
  ));

  const resolvedBackNeckDepth = roundEighth(clampMin(
    overrideBNDEnabled ? n(measurements.overrideBND, autoBackNeckDepth) : autoBackNeckDepth,
    0.25,
  ));

  const resolvedFrontNeckDepthInput = roundEighth(clampMin(
    overrideFNDEnabled ? n(measurements.overrideFND, autoFrontNeckDepth) : autoFrontNeckDepth,
    0.25,
  ));

  const xCF = roundEighth(chest / 2);

  const neckCurves = buildNeckCurves({
    neck,
    xCF,
    backToFtHps,
    frontNeckWidth: resolvedFrontNeckWidth,
    backNeckWidth: resolvedBackNeckWidth,
    backNeckDepth: resolvedBackNeckDepth,
    frontNeckDepth: resolvedFrontNeckDepthInput,
    useFrontDepthOverride: overrideFNDEnabled,
  });

  const {
    CB1,
    BHPS,
    CF0,
    FHPS,
    CF1,
    backCtrl: backNeckCtrlBase,
    frontCtrl: frontNeckCtrlBase,
    resolvedFrontNeckDepth,
    targetHalfNeckMin,
    targetHalfNeckMax,
  } = neckCurves;

  const CB0 = rp(0, 0);

  const BLPS = rp(shoulder / 2, backShoulderSlope);
  const backShoulderLength = dist(BHPS, BLPS);
  const backShoulderAngleDegrees = angleFromHorizontalDegrees(BHPS, BLPS);

  const flpsYOffset = frontShoulderSlope;
  const flpsXOffset = Math.sqrt(Math.max(0.25, backShoulderLength ** 2 - flpsYOffset ** 2));
  const FLPS = rp(FHPS.x - flpsXOffset, FHPS.y + flpsYOffset);
  const frontShoulderAngleDegrees = angleFromHorizontalDegrees(FHPS, FLPS);

  const chestY = roundEighth(armholeScyeDepth + (BLPS.y + FLPS.y) / 2);
  const CB3 = rp(0, chestY);
  const CF3 = rp(xCF, chestY);
  const autoAcrossBackDepth = roundEighth((CB3.y - CB1.y) * 0.62);
  const resolvedAcrossBackDepth = roundEighth(clampMin(
    overrideAcrossBackDepthEnabled ? n(measurements.overrideAcrossBackDepth, autoAcrossBackDepth) : autoAcrossBackDepth,
    0.25,
  ));
  const CB2 = rp(0, CB1.y + resolvedAcrossBackDepth);

  const autoAcrossFrontDepth = roundEighth((chestY - xfDistanceFromChest) - CF1.y);
  const resolvedAcrossFrontDepth = roundEighth(clampMin(
    overrideAcrossFrontDepthEnabled ? n(measurements.overrideAcrossFrontDepth, autoAcrossFrontDepth) : autoAcrossFrontDepth,
    0.25,
  ));
  const CF2 = rp(xCF, CF1.y + resolvedAcrossFrontDepth);

  const CB4 = rp(0, FHPS.y + distanceToWaist);
  const CF4 = rp(xCF, FHPS.y + distanceToWaist);
  const CB5 = rp(0, FHPS.y + distanceToBelly);
  const CF5 = rp(xCF, FHPS.y + distanceToBelly);

  const shirtLengthFrontAuto = roundEighth(Math.max(1, shirtLengthBack - backToFtHps));
  const CB7 = rp(0, BHPS.y + shirtLengthBack);
  const CB6 = rp(0, CB7.y - tailDepthBack);
  const CF6 = rp(xCF, CB6.y);
  const CF7 = rp(xCF, CF6.y + tailDepthFront);
  const E1 = rp(CF1.x + buttonExtensionWidth, CF1.y);
  const E7 = rp(CF7.x + buttonExtensionWidth, CF7.y);
  const PL1 = rp(E1.x + placketFoldWidth, CF1.y);
  const PL7 = rp(E7.x + placketFoldWidth, CF7.y);

  const backChestWidth = roundEighth(clampMin(chest / 4, 0.75));
  const frontChestWidth = roundEighth(clampMin(chest / 4, 0.75));

  const frontChestSplitOverride = Number.isFinite(Number(measurements.frontChestSplit))
    ? Number(measurements.frontChestSplit)
    : null;

  const baseBackWaistWidth = roundEighth(clampMin(waist / 4, 0.75));
  const baseFrontWaistWidth = roundEighth(clampMin(waist / 4, 0.75));
  const baseBackBellyWidth = roundEighth(clampMin(belly / 4, 0.75));
  const baseFrontBellyWidth = roundEighth(clampMin(belly / 4, 0.75));

  const waistDartIntake = roundEighth(dartWidthWaist);
  const bellyDartIntake = roundEighth(dartWidthBelly);
  const targetWaistHalf = roundEighth(waist / 2);
  const targetBellyHalf = roundEighth(belly / 2);

  const waistAdjusted = adjustPairSimultaneously(
    baseFrontWaistWidth,
    baseBackWaistWidth,
    waistDartIntake,
    targetWaistHalf,
  );

  const bellyAdjusted = adjustPairSimultaneously(
    baseFrontBellyWidth,
    baseBackBellyWidth,
    bellyDartIntake,
    targetBellyHalf,
  );

  const frontWaistWidth = waistAdjusted.front;
  const backWaistWidth = waistAdjusted.back;
  const frontBellyWidth = bellyAdjusted.front;
  const backBellyWidth = bellyAdjusted.back;

  const frontWaistSplitOverride = Number.isFinite(Number(measurements.frontWaistSplit))
    ? Number(measurements.frontWaistSplit)
    : null;
  const frontBellySplitOverride = Number.isFinite(Number(measurements.frontBellySplit))
    ? Number(measurements.frontBellySplit)
    : null;

  const frontSeatSplitOverride = Number.isFinite(Number(measurements.frontSeatSplit))
    ? Number(measurements.frontSeatSplit)
    : null;

  const backSeatWidth = roundEighth(clampMin(seat / 4, 0.75));
  const frontSeatWidth = roundEighth(clampMin(seat / 4, 0.75));

  const chestSplit = resolveFrontBackSplitByFront({
    targetHalf: chest / 2,
    preferredFront: frontChestSplitOverride ?? frontChestWidth,
  });

  let waistSplit = resolveFrontBackSplitByFront({
    targetHalf: targetWaistHalf,
    preferredFront: frontWaistSplitOverride ?? frontWaistWidth,
  });

  let bellySplit = resolveFrontBackSplitByFront({
    targetHalf: targetBellyHalf,
    preferredFront: frontBellySplitOverride ?? frontBellyWidth,
  });

  if (applyNetCompensatedDartSplit) {
    const portlyDelta = (() => {
      if (portlyType === 'thin') return 0;
      if (portlyType === 'slight_belly') return 1;
      if (portlyType === 'big_belly') return 1.5;
      return 0.5;
    })();

    const resolveCompensatedRawSplit = ({
      targetHalf,
      dartIntake,
      frontOverride,
      min = 0.75,
    }) => {
      const frontNetTarget = (targetHalf + portlyDelta) / 2;
      const backNetTarget = (targetHalf - portlyDelta) / 2;
      const frontRawFromNet = frontNetTarget + (dartIntake / 2);
      const backRawFromNet = backNetTarget + (dartIntake / 2);
      const rawTargetHalf = targetHalf + dartIntake;

      const preferredFront = Number.isFinite(Number(frontOverride))
        ? Number(frontOverride)
        : frontRawFromNet;

      const clampedFront = clamp(
        preferredFront,
        min,
        Math.max(min, rawTargetHalf - min),
      );

      return {
        front: clampedFront,
        back: rawTargetHalf - clampedFront,
        targetHalf,
        rawHalf: rawTargetHalf,
        frontNet: clampedFront - dartIntake,
        backNet: rawTargetHalf - clampedFront,
      };
    };

    waistSplit = resolveCompensatedRawSplit({
      targetHalf: targetWaistHalf,
      dartIntake: waistDartIntake,
      frontOverride: frontWaistSplitOverride,
    });

    bellySplit = resolveCompensatedRawSplit({
      targetHalf: targetBellyHalf,
      dartIntake: bellyDartIntake,
      frontOverride: frontBellySplitOverride,
    });
  }

  const seatSplit = resolveFrontBackSplitByFront({
    targetHalf: seat / 2,
    preferredFront: frontSeatSplitOverride ?? frontSeatWidth,
  });

  const frontChestWidthResolved = chestSplit.front;
  const backChestWidthResolved = chestSplit.back;
  const frontWaistWidthResolved = waistSplit.front;
  const backWaistWidthResolved = waistSplit.back;
  const frontBellyWidthResolved = bellySplit.front;
  const backBellyWidthResolved = bellySplit.back;
  const frontSeatWidthResolved = seatSplit.front;
  const backSeatWidthResolved = seatSplit.back;

  const BCP = rp(backChestWidthResolved, CB3.y);
  const FCP = rp(xCF - frontChestWidthResolved, CF3.y);
  let BWP = rp(backWaistWidthResolved, CB4.y);
  let FWP = rp(xCF - frontWaistWidthResolved, CF4.y);
  let BBP = rp(backBellyWidthResolved, CB5.y);
  let FBP = rp(xCF - frontBellyWidthResolved, CF5.y);
  let BSP = rp(backSeatWidthResolved, CB6.y);
  let FSP = rp(xCF - frontSeatWidthResolved, CF6.y);

  const SIDE_SYNC_TOLERANCE = 0.0625;
  const SIDE_SYNC_TARGET_TOLERANCE = SIDE_SYNC_TOLERANCE * 0.5;

  const segmentLength = (p1, p2) => Math.hypot(p2.x - p1.x, p2.y - p1.y);

  const solveEndpointYByLength = (startPoint, endPoint, targetLength) => {
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const minLength = Math.abs(dx);
    const safeTarget = Math.max(minLength, targetLength);
    const absDy = Math.sqrt(Math.max(0, safeTarget * safeTarget - dx * dx));
    const sign = Math.sign(dy) || 1;
    return rp(endPoint.x, startPoint.y + sign * absDy);
  };

  const minSegmentLengthByYShift = (startPoint, endPoint) => Math.abs(endPoint.x - startPoint.x);

  const syncSegmentByCascade = ({
    frontStart,
    frontEnd,
    backStart,
    backEnd,
    applyFrontCascade,
    applyBackCascade,
  }) => {
    const recalcDiff = () => segmentLength(frontStart(), frontEnd()) - segmentLength(backStart(), backEnd());

    let diff = recalcDiff();
    if (Math.abs(diff) <= SIDE_SYNC_TOLERANCE) return;

    if (diff > SIDE_SYNC_TOLERANCE) {
      // Step 1: shorten longer front side as much as possible toward tolerance.
      {
        const start = frontStart();
        const end = frontEnd();
        const backLenNow = segmentLength(backStart(), backEnd());
        const targetLen = backLenNow + SIDE_SYNC_TARGET_TOLERANCE;
        const next = solveEndpointYByLength(start, end, targetLen);
        const dy = next.y - end.y;
        applyFrontCascade(next, dy);
      }

      diff = recalcDiff();
      if (diff <= SIDE_SYNC_TOLERANCE) return;

      // Step 2 fallback: if still out-of-sync, lengthen shorter back side to within tolerance.
      {
        const start = backStart();
        const end = backEnd();
        const frontLenNow = segmentLength(frontStart(), frontEnd());
        const targetLen = Math.max(
          minSegmentLengthByYShift(start, end),
          frontLenNow - SIDE_SYNC_TARGET_TOLERANCE,
        );
        const next = solveEndpointYByLength(start, end, targetLen);
        const dy = next.y - end.y;
        applyBackCascade(next, dy);
      }
      return;
    }

    // back longer
    {
      // Step 1: shorten longer back side
      const start = backStart();
      const end = backEnd();
      const frontLenNow = segmentLength(frontStart(), frontEnd());
      const targetLen = frontLenNow + SIDE_SYNC_TARGET_TOLERANCE;
      const next = solveEndpointYByLength(start, end, targetLen);
      const dy = next.y - end.y;
      applyBackCascade(next, dy);
    }

    diff = recalcDiff();
    if (Math.abs(diff) <= SIDE_SYNC_TOLERANCE) return;

    // Step 2 fallback: lengthen shorter front side
    {
      const start = frontStart();
      const end = frontEnd();
      const backLenNow = segmentLength(backStart(), backEnd());
      const targetLen = Math.max(
        minSegmentLengthByYShift(start, end),
        backLenNow - SIDE_SYNC_TARGET_TOLERANCE,
      );
      const next = solveEndpointYByLength(start, end, targetLen);
      const dy = next.y - end.y;
      applyFrontCascade(next, dy);
    }
  };

  syncSegmentByCascade({
    frontStart: () => FCP,
    frontEnd: () => FWP,
    backStart: () => BCP,
    backEnd: () => BWP,
    applyFrontCascade: (newPoint, deltaY) => {
      FWP = newPoint;
      FBP = rp(FBP.x, FBP.y + deltaY);
      FSP = rp(FSP.x, FSP.y + deltaY);
    },
    applyBackCascade: (newPoint, deltaY) => {
      BWP = newPoint;
      BBP = rp(BBP.x, BBP.y + deltaY);
      BSP = rp(BSP.x, BSP.y + deltaY);
    },
  });

  syncSegmentByCascade({
    frontStart: () => FWP,
    frontEnd: () => FBP,
    backStart: () => BWP,
    backEnd: () => BBP,
    applyFrontCascade: (newPoint, deltaY) => {
      FBP = newPoint;
      FSP = rp(FSP.x, FSP.y + deltaY);
    },
    applyBackCascade: (newPoint, deltaY) => {
      BBP = newPoint;
      BSP = rp(BSP.x, BSP.y + deltaY);
    },
  });

  syncSegmentByCascade({
    frontStart: () => FBP,
    frontEnd: () => FSP,
    backStart: () => BBP,
    backEnd: () => BSP,
    applyFrontCascade: (newPoint) => {
      FSP = newPoint;
    },
    applyBackCascade: (newPoint) => {
      BSP = newPoint;
    },
  });
  const backHemAnchorOffset = roundEighth(dist(CB6, BSP) * 0.25);
  const frontHemAnchorOffset = roundEighth(dist(CF7, FSP) * 0.25);
  const BHP = rp(CB7.x + backHemAnchorOffset, CB7.y);
  const FHP = rp(CF7.x - frontHemAnchorOffset, CF7.y);

  const BXB = rp(acrossBack / 2, CB2.y);
  const FXF = rp(xCF - acrossFront / 2, CF2.y);

  [
    ['CF0', CF0],
    ['CF1', CF1],
    ['CF2', CF2],
    ['CF3', CF3],
    ['CF4', CF4],
    ['CF5', CF5],
    ['CF6', CF6],
    ['CF7', CF7],
    ['FHPS', FHPS],
    ['FLPS', FLPS],
    ['FXF', FXF],
    ['FCP', FCP],
    ['FWP', FWP],
    ['FBP', FBP],
    ['FSP', FSP],
    ['FHP', FHP],
    ['E1', E1],
    ['E7', E7],
    ['PL1', PL1],
    ['PL7', PL7],
    ['CB0', CB0],
    ['CB1', CB1],
    ['CB2', CB2],
    ['CB3', CB3],
    ['CB4', CB4],
    ['CB5', CB5],
    ['CB6', CB6],
    ['CB7', CB7],
    ['BHPS', BHPS],
    ['BLPS', BLPS],
    ['BXB', BXB],
    ['BCP', BCP],
    ['BWP', BWP],
    ['BBP', BBP],
    ['BSP', BSP],
    ['BHP', BHP],
  ].forEach(([pointName, point]) => applyPointDeltaInPlace(pointName, point));

  const backCtrl = rp(backNeckCtrlBase.x + backNeckCtrlDx, backNeckCtrlBase.y + backNeckCtrlDy);

  const smoothBhps = yokeEnabled && frontNeckRollover > 1e-6;
  const smoothBlps = yokeEnabled && frontArmholeRollover > 1e-6;

  // Decoupled front neck control base:
  // keep front neck behavior independent from back neck curve edits.
  // Front base stays tied to the front neck solver output only.
  const frontCtrlBase = rp(frontNeckCtrlBase.x, CF1.y);
  const frontCtrl = rp(frontCtrlBase.x + frontNeckCtrlDx, frontCtrlBase.y + frontNeckCtrlDy);
  const backNeckLenAdjusted = quadLength(CB1, backCtrl, BHPS);
  const frontNeckLenAdjusted = quadLength(CF1, frontCtrl, FHPS);
  const halfNeckLengthAdjusted = backNeckLenAdjusted + frontNeckLenAdjusted;

  const backArmCtrl1Base = rp(
    BLPS.x + (BXB.x - BLPS.x) * 0.38,
    BLPS.y + (BXB.y - BLPS.y) * 0.32,
  );

  const frontArmCtrl1Base = rp(
    FLPS.x + (FXF.x - FLPS.x) * 0.42,
    FLPS.y + (FXF.y - FLPS.y) * 0.34,
  );

  const backJoinVector = { x: BXB.x - backArmCtrl1Base.x, y: BXB.y - backArmCtrl1Base.y };
  const backJoinUnit = normalizeVector(backJoinVector);
  const backLowerMagnitude = Math.max(0.5, dist(BXB, BCP) * 0.72);
  const backArmCtrl2Base = rp(
    BXB.x + backJoinUnit.x * backLowerMagnitude,
    BXB.y + backJoinUnit.y * backLowerMagnitude,
  );

  const frontJoinVector = { x: FXF.x - frontArmCtrl1Base.x, y: FXF.y - frontArmCtrl1Base.y };
  const frontJoinUnit = normalizeVector(frontJoinVector);
  const frontLowerMagnitude = Math.max(0.5, dist(FXF, FCP) * 0.72);
  const frontArmCtrl2Base = rp(
    FXF.x + frontJoinUnit.x * frontLowerMagnitude,
    FXF.y + frontJoinUnit.y * frontLowerMagnitude,
  );

  let backArmCtrl1 = rp(backArmCtrl1Base.x + backArmPart1CtrlDx, backArmCtrl1Base.y + backArmPart1CtrlDy);
  let backArmCtrl2 = rp(backArmCtrl2Base.x + backArmPart2CtrlDx, backArmCtrl2Base.y + backArmPart2CtrlDy);
  let frontArmCtrl1 = rp(frontArmCtrl1Base.x + frontArmPart1CtrlDx, frontArmCtrl1Base.y + frontArmPart1CtrlDy);
  let frontArmCtrl2 = rp(frontArmCtrl2Base.x + frontArmPart2CtrlDx, frontArmCtrl2Base.y + frontArmPart2CtrlDy);

  const frontArmCtrl2InitialY = frontArmCtrl2.y;
  const backArmCtrl2InitialY = backArmCtrl2.y;

  const shoulderBackToCb = rp(CB0.x, BLPS.y);
  const frontScyeBottom = rp(FLPS.x, CF3.y);
  const backScyeBottom = rp(BLPS.x, CB3.y);

  const backHemSpan = Math.max(0.5, dist(BSP, BHP));
  const frontHemSpan = Math.max(0.5, dist(FSP, FHP));
  const seamBlendSpan = Math.max(0.5, (backHemSpan + frontHemSpan) / 2);

  // Seam join smoothness at BSP/FSP:
  // keep default tangents horizontal and mirrored so front/back hem edges blend
  // smoothly when BSP/FSP are matched in sewing.
  const sharedHemStartMag = hemCurveMode >= 0.5 ? seamBlendSpan * 0.36 : seamBlendSpan * 0.24;
  const backHemEndMag = hemCurveMode >= 0.5 ? backHemSpan * 0.54 : backHemSpan * 0.32;
  const frontHemEndMag = hemCurveMode >= 0.5 ? frontHemSpan * 0.54 : frontHemSpan * 0.32;

  const backHemCtrl1Base = rp(BSP.x - sharedHemStartMag, BSP.y);
  const backHemCtrl2Base = rp(BHP.x + backHemEndMag, BHP.y);

  const frontHemCtrl1Base = rp(FSP.x + sharedHemStartMag, FSP.y);
  const frontHemCtrl2Base = rp(FHP.x - frontHemEndMag, FHP.y);

  const backHemCtrl1 = rp(backHemCtrl1Base.x + backHemCtrl1Dx, backHemCtrl1Base.y + backHemCtrl1Dy);
  const backHemCtrl2 = rp(backHemCtrl2Base.x + backHemCtrl2Dx, backHemCtrl2Base.y + backHemCtrl2Dy);
  const frontHemCtrl1 = rp(frontHemCtrl1Base.x + frontHemCtrl1Dx, frontHemCtrl1Base.y + frontHemCtrl1Dy);
  const frontHemCtrl2 = rp(frontHemCtrl2Base.x + frontHemCtrl2Dx, frontHemCtrl2Base.y + frontHemCtrl2Dy);

  const dartCenterX = roundEighth((CB4.x + BWP.x) / 2);

  const BD1 = rp(dartCenterX - dartWidthWaist / 2, CB4.y);
  const BD2 = rp(dartCenterX + dartWidthWaist / 2, CB4.y);
  const BD3 = rp(dartCenterX - dartWidthBelly / 2, CB5.y);
  const BD4 = rp(dartCenterX + dartWidthBelly / 2, CB5.y);
  const BD0 = rp(dartCenterX, CB4.y - dartLengthTop);
  const BD5 = rp(dartCenterX, CB5.y + dartLengthBottom);
  [
    ['BD0', BD0],
    ['BD1', BD1],
    ['BD2', BD2],
    ['BD3', BD3],
    ['BD4', BD4],
    ['BD5', BD5],
  ].forEach(([pointName, point]) => applyPointDeltaInPlace(pointName, point));

  const intersectLines = (a1, a2, b1, b2) => {
    const dxa = a2.x - a1.x;
    const dya = a2.y - a1.y;
    const dxb = b2.x - b1.x;
    const dyb = b2.y - b1.y;
    const det = dxa * dyb - dya * dxb;

    if (Math.abs(det) < 1e-6) return null;

    const t = ((b1.x - a1.x) * dyb - (b1.y - a1.y) * dxb) / det;
    return rp(
      a1.x + dxa * t,
      a1.y + dya * t,
    );
  };

  const createThreeSegmentSideControls = ({
    p0,
    p1,
    p2,
    p3,
    interiorSign,
    deltaCW,
    deltaWB,
    deltaBS,
    chestWaistDx,
    chestWaistDy,
    waistBellyDx,
    waistBellyDy,
    bellySeatDx,
    bellySeatDy,
    useTurnPoints = false,
  }) => {
    const outsideSign = -interiorSign;
    const localDeltaCW = deltaCW / 4;
    const localDeltaWB = deltaWB / 4;
    const localDeltaBS = deltaBS / 4;

    const c01BaseX = deltaCW < 0
      ? p1.x + interiorSign * clamp(Math.abs(localDeltaCW * 0.6), 0.35, 0.6)
      : p0.x + outsideSign * clamp(Math.max(0.4, Math.abs(localDeltaCW * 0.6)), 0.4, 0.9);

    const c01BaseY = deltaCW < 0
      ? (p0.y + p1.y) / 2
      : p0.y + (p1.y - p0.y) * 0.38;

    const c01Base = rp(c01BaseX, c01BaseY);

    const c23BaseY = rp(
      0,
      (p2.y + p3.y) / 2 + clamp(localDeltaBS * 0.12, -0.5, 0.5),
    ).y;

    // Strict 90° entry at seat requires control X to align with seat X.
    const c23Base = rp(
      p3.x,
      c23BaseY,
    );

    // G1 continuity lock at waist and belly:
    // - line through (CP_A ↔ Waist)
    // - line through (Belly ↔ CP_C)
    // CP_B sits at their intersection.
    const c12Intersection = intersectLines(c01Base, p1, p2, c23Base);
    const c12Fallback = rp(
      p1.x + outsideSign * clamp(localDeltaWB * 0.6, -0.8, 0.8),
      (p1.y + p2.y) / 2,
    );

    const c12Raw = useTurnPoints ? c12Fallback : (c12Intersection || c12Fallback);
    const c12Base = rp(
      clamp(c12Raw.x, Math.min(p1.x, p2.x) - 0.75, Math.max(p1.x, p2.x) + 0.75),
      (p1.y + p2.y) / 2,
    );

    return {
      chestWaist: rp(c01Base.x + chestWaistDx, c01Base.y + chestWaistDy),
      waistBelly: rp(c12Base.x + waistBellyDx, c12Base.y + waistBellyDy),
      bellySeat: rp(c23Base.x + bellySeatDx, c23Base.y + bellySeatDy),
    };
  };

  // front interior is toward +X (toward CF), back interior toward -X (toward CB)
  const frontSideControls = createThreeSegmentSideControls({
    p0: FCP,
    p1: FWP,
    p2: FBP,
    p3: FSP,
    interiorSign: 1,
    deltaCW: waist - chest,
    deltaWB: belly - waist,
    deltaBS: seat - belly,
    chestWaistDx: frontChestWaistCtrlDx,
    chestWaistDy: frontChestWaistCtrlDy,
    waistBellyDx: frontWaistBellyCtrlDx,
    waistBellyDy: frontWaistBellyCtrlDy,
    bellySeatDx: frontBellySeatCtrlDx,
    bellySeatDy: frontBellySeatCtrlDy,
    useTurnPoints: true,
  });

  const backSideControls = createThreeSegmentSideControls({
    p0: BCP,
    p1: BWP,
    p2: BBP,
    p3: BSP,
    interiorSign: -1,
    deltaCW: waist - chest,
    deltaWB: belly - waist,
    deltaBS: seat - belly,
    chestWaistDx: backChestWaistCtrlDx,
    chestWaistDy: backChestWaistCtrlDy,
    waistBellyDx: backWaistBellyCtrlDx,
    waistBellyDy: backWaistBellyCtrlDy,
    bellySeatDx: backBellySeatCtrlDx,
    bellySeatDy: backBellySeatCtrlDy,
    useTurnPoints: true,
  });

  let frontChestWaistCtrl = frontSideControls.chestWaist;
  let frontWaistBellyCtrl = frontSideControls.waistBelly;
  let frontBellySeatCtrl = frontSideControls.bellySeat;
  let backChestWaistCtrl = backSideControls.chestWaist;
  let backWaistBellyCtrl = backSideControls.waistBelly;
  let backBellySeatCtrl = backSideControls.bellySeat;

  if (!decoupleArmholeSplitControls) {
    const smoothedFXF = smoothQuadraticTurnPoint({
      prevPoint: FLPS,
      turnPoint: FXF,
      nextPoint: FCP,
      incomingControl: frontArmCtrl1,
      outgoingControl: frontArmCtrl2,
    });
    frontArmCtrl1 = smoothedFXF.incomingControl;
    frontArmCtrl2 = smoothedFXF.outgoingControl;

    const smoothedBXB = smoothQuadraticTurnPoint({
      prevPoint: BLPS,
      turnPoint: BXB,
      nextPoint: BCP,
      incomingControl: backArmCtrl1,
      outgoingControl: backArmCtrl2,
    });
    backArmCtrl1 = smoothedBXB.incomingControl;
    backArmCtrl2 = smoothedBXB.outgoingControl;
  }

  if (preserveArmPart2CtrlYOnXDrag) {
    frontArmCtrl2 = rp(frontArmCtrl2.x, frontArmCtrl2InitialY);
    backArmCtrl2 = rp(backArmCtrl2.x, backArmCtrl2InitialY);
  }

  const backBaseShoulderJoinCommand = buildShoulderJoinCommand({
    startPoint: BHPS,
    endPoint: BLPS,
    startSmooth: smoothBhps,
    endSmooth: smoothBlps,
    startTangent: {
      x: BHPS.x - backCtrl.x,
      y: BHPS.y - backCtrl.y,
    },
    endTangent: {
      x: backArmCtrl1.x - BLPS.x,
      y: backArmCtrl1.y - BLPS.y,
    },
  });

  const frontSideChestToWaist = segmentLength(FCP, FWP);
  const backSideChestToWaist = segmentLength(BCP, BWP);
  const frontSideWaistToBelly = segmentLength(FWP, FBP);
  const backSideWaistToBelly = segmentLength(BWP, BBP);
  const frontSideBellyToSeat = segmentLength(FBP, FSP);
  const backSideBellyToSeat = segmentLength(BBP, BSP);

  const frontBasePath = `
    M ${toSVG(CF1)}
    Q ${toSVG(frontCtrl)} ${toSVG(FHPS)}
    L ${toSVG(FLPS)}
    Q ${toSVG(frontArmCtrl1)} ${toSVG(FXF)}
    Q ${toSVG(frontArmCtrl2)} ${toSVG(FCP)}
    Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FWP)}
    Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(FBP)}
    Q ${toSVG(frontBellySeatCtrl)} ${toSVG(FSP)}
    C ${toSVG(frontHemCtrl1)} ${toSVG(frontHemCtrl2)} ${toSVG(FHP)}
    L ${toSVG(CF7)}
    L ${toSVG(CF1)}
    Z
  `;

  const backBasePath = `
    M ${toSVG(CB1)}
    Q ${toSVG(backCtrl)} ${toSVG(BHPS)}
    ${backBaseShoulderJoinCommand}
    Q ${toSVG(backArmCtrl1)} ${toSVG(BXB)}
    Q ${toSVG(backArmCtrl2)} ${toSVG(BCP)}
    Q ${toSVG(backChestWaistCtrl)} ${toSVG(BWP)}
    Q ${toSVG(backWaistBellyCtrl)} ${toSVG(BBP)}
    Q ${toSVG(backBellySeatCtrl)} ${toSVG(BSP)}
    C ${toSVG(backHemCtrl1)} ${toSVG(backHemCtrl2)} ${toSVG(BHP)}
    L ${toSVG(CB7)}
    L ${toSVG(CB1)}
    Z
  `;

  const frontNeckTotal = quadLength(CF1, frontCtrl, FHPS, 56);
  const neckRolloverDistance = clamp(frontNeckRollover, 0, frontNeckTotal);
  const frontNeckSplit = splitQuadraticAtDistanceFromEnd(CF1, frontCtrl, FHPS, neckRolloverDistance);

  const frontNeckLowerSegments = neckRolloverDistance >= frontNeckTotal - 1e-6
    ? []
    : [frontNeckSplit.left];
  const frontNeckUpperSegments = neckRolloverDistance <= 1e-6
    ? []
    : [frontNeckSplit.right];
  const frontNeckCut = frontNeckSplit.splitPoint;

  const frontArmSeg1 = { p0: FLPS, p1: frontArmCtrl1, p2: FXF };
  const frontArmSeg2 = { p0: FXF, p1: frontArmCtrl2, p2: FCP };
  const frontArmSeg1Len = quadLength(frontArmSeg1.p0, frontArmSeg1.p1, frontArmSeg1.p2, 56);
  const frontArmSeg2Len = quadLength(frontArmSeg2.p0, frontArmSeg2.p1, frontArmSeg2.p2, 56);
  const frontArmTotal = frontArmSeg1Len + frontArmSeg2Len;
  const armholeRolloverDistance = clamp(frontArmholeRollover, 0, frontArmTotal);

  let frontArmUpperSegments = [];
  let frontArmLowerSegments = [];
  let frontArmCut = FLPS;

  if (armholeRolloverDistance <= 1e-6) {
    frontArmUpperSegments = [];
    frontArmLowerSegments = [frontArmSeg1, frontArmSeg2];
    frontArmCut = FLPS;
  } else if (armholeRolloverDistance < frontArmSeg1Len - 1e-6) {
    const splitOnPart1 = splitQuadraticAtDistanceFromStart(
      frontArmSeg1.p0,
      frontArmSeg1.p1,
      frontArmSeg1.p2,
      armholeRolloverDistance,
    );
    frontArmUpperSegments = [splitOnPart1.left];
    frontArmLowerSegments = [splitOnPart1.right, frontArmSeg2];
    frontArmCut = splitOnPart1.splitPoint;
  } else if (armholeRolloverDistance <= frontArmSeg1Len + 1e-6) {
    frontArmUpperSegments = [frontArmSeg1];
    frontArmLowerSegments = [frontArmSeg2];
    frontArmCut = FXF;
  } else if (armholeRolloverDistance < frontArmTotal - 1e-6) {
    const splitOnPart2 = splitQuadraticAtDistanceFromStart(
      frontArmSeg2.p0,
      frontArmSeg2.p1,
      frontArmSeg2.p2,
      armholeRolloverDistance - frontArmSeg1Len,
    );
    frontArmUpperSegments = [frontArmSeg1, splitOnPart2.left];
    frontArmLowerSegments = [splitOnPart2.right];
    frontArmCut = splitOnPart2.splitPoint;
  } else {
    frontArmUpperSegments = [frontArmSeg1, frontArmSeg2];
    frontArmLowerSegments = [];
    frontArmCut = FCP;
  }
  applyPointDeltaInPlace('FNY', frontNeckCut);
  applyPointDeltaInPlace('FAY', frontArmCut);

  const yokeCutY = roundEighth(CB1.y + yokeDepth);
  const CBY = rp(CB1.x, yokeCutY);
  applyPointDeltaInPlace('CBY', CBY);

  const backArmPart1Points = sampleQuadratic(BLPS, backArmCtrl1, BXB, 64);
  const backArmPart2Points = sampleQuadratic(BXB, backArmCtrl2, BCP, 64);
  const backArmCurvePoints = [...backArmPart1Points, ...backArmPart2Points.slice(1)];
  const backArmSplit = splitPolylineAtY(backArmCurvePoints, yokeCutY);

  const backArmYokeCut = {
    x: backArmSplit.splitPoint.x,
    y: backArmSplit.splitPoint.y,
  };
  applyPointDeltaInPlace('BYA', backArmYokeCut);

  const backArmUpperPoints = [...backArmSplit.upper];
  const backArmLowerPoints = [...backArmSplit.lower];

  const hasBackYokeDart = yokeEnabled && yokeDartWidth > 0;
  const halfYokeDartWidth = yokeDartWidth / 2;
  const yokeDartMidpoint = rp((CBY.x + backArmYokeCut.x) / 2, (CBY.y + backArmYokeCut.y) / 2);
  const backPanelDartMidpoint = rp((CBY.x + backArmYokeCut.x) / 2, (CBY.y + backArmYokeCut.y) / 2);
  applyPointDeltaInPlace('BYM', yokeDartMidpoint);

  const yokeDartDirection = normalizeOrFallback({
    x: BLPS.x - backArmYokeCut.x,
    y: BLPS.y - backArmYokeCut.y,
  }, { x: -1, y: 0 });

  const backPanelDartDirection = normalizeOrFallback({
    x: BXB.x - backArmYokeCut.x,
    y: BXB.y - backArmYokeCut.y,
  }, { x: 1, y: 0 });

  const yokeDartBya = hasBackYokeDart
    ? rp(
      backArmYokeCut.x + yokeDartDirection.x * halfYokeDartWidth,
      backArmYokeCut.y + yokeDartDirection.y * halfYokeDartWidth,
    )
    : backArmYokeCut;

  const backPanelDartBya = hasBackYokeDart
    ? rp(
      backArmYokeCut.x + backPanelDartDirection.x * halfYokeDartWidth,
      backArmYokeCut.y + backPanelDartDirection.y * halfYokeDartWidth,
    )
    : backArmYokeCut;
  if (hasBackYokeDart) {
    applyPointDeltaInPlace('BYDY', yokeDartBya);
    applyPointDeltaInPlace('BYDB', backPanelDartBya);
  }

  if (backArmUpperPoints.length > 0) {
    backArmUpperPoints[backArmUpperPoints.length - 1] = hasBackYokeDart ? yokeDartBya : backArmYokeCut;
  }
  if (backArmLowerPoints.length > 0) {
    backArmLowerPoints[0] = hasBackYokeDart ? backPanelDartBya : backArmYokeCut;
  }

  const yokeDartControlBase = {
    x: (yokeDartMidpoint.x + yokeDartBya.x) / 2,
    y: (yokeDartMidpoint.y + yokeDartBya.y) / 2,
  };

  let yokeDartControl = hasBackYokeDart
    ? rp(
      yokeDartControlBase.x + yokeDartYokeCtrlDx,
      yokeDartControlBase.y + yokeDartYokeCtrlDy,
    )
    : yokeDartControlBase;
  const yokeSegmentMid = {
    x: (yokeDartMidpoint.x + yokeDartBya.x) / 2,
    y: (yokeDartMidpoint.y + yokeDartBya.y) / 2,
  };
  const yokeSegmentDir = normalizeOrFallback(
    {
      x: yokeDartBya.x - yokeDartMidpoint.x,
      y: yokeDartBya.y - yokeDartMidpoint.y,
    },
    { x: 0, y: 1 },
  );
  const yokeSegmentNormal = { x: -yokeSegmentDir.y, y: yokeSegmentDir.x };

  const yokeControlVectorRaw = {
    x: yokeDartControl.x - yokeSegmentMid.x,
    y: yokeDartControl.y - yokeSegmentMid.y,
  };

  const yokeTangentComponentRaw = dot(yokeControlVectorRaw, yokeSegmentDir);
  const yokeNormalComponentRaw = dot(yokeControlVectorRaw, yokeSegmentNormal);

  const yokeControlVector = {
    x: yokeDartControl.x - yokeSegmentMid.x,
    y: yokeDartControl.y - yokeSegmentMid.y,
  };

  const yokeTangentComponent = dot(yokeControlVector, yokeSegmentDir);
  const yokeNormalComponent = dot(yokeControlVector, yokeSegmentNormal);

  const backSegmentMid = {
    x: (backPanelDartMidpoint.x + backPanelDartBya.x) / 2,
    y: (backPanelDartMidpoint.y + backPanelDartBya.y) / 2,
  };
  const backSegmentDir = normalizeOrFallback(
    {
      x: backPanelDartBya.x - backPanelDartMidpoint.x,
      y: backPanelDartBya.y - backPanelDartMidpoint.y,
    },
    { x: 0, y: 1 },
  );
  const backSegmentNormal = { x: -backSegmentDir.y, y: backSegmentDir.x };

  const backPanelDartControlMirrorBase = rp(
    backSegmentMid.x + backSegmentDir.x * yokeTangentComponent - backSegmentNormal.x * yokeNormalComponent,
    backSegmentMid.y + backSegmentDir.y * yokeTangentComponent - backSegmentNormal.y * yokeNormalComponent,
  );

  const backPanelDartControl = hasBackYokeDart
    ? rp(
      backPanelDartControlMirrorBase.x + yokeDartBackCtrlDx,
      backPanelDartControlMirrorBase.y + yokeDartBackCtrlDy,
    )
    : backPanelDartControlMirrorBase;

  const backYokeArmBezierControlBase = rp(
    (BLPS.x + yokeDartBya.x + backArmCtrl1.x) / 3,
    (BLPS.y + yokeDartBya.y + backArmCtrl1.y) / 3,
  );
  const backPanelYokeJoinControlBase = rp(
    (backPanelDartBya.x + BXB.x + backArmCtrl1.x) / 3,
    (backPanelDartBya.y + BXB.y + backArmCtrl1.y) / 3,
  );

  const backYokeArmBezierControl = hasBackYokeDart
    ? rp(
      backYokeArmBezierControlBase.x + backYokeArmSplitCtrlDx,
      backYokeArmBezierControlBase.y + backYokeArmSplitCtrlDy,
    )
    : backYokeArmBezierControlBase;

  const backPanelYokeJoinControl = hasBackYokeDart
    ? rp(
      backPanelYokeJoinControlBase.x + backPanelYokeSplitCtrlDx,
      backPanelYokeJoinControlBase.y + backPanelYokeSplitCtrlDy,
    )
    : backPanelYokeJoinControlBase;

  const canApplyBackPleatShift = ['side_pleat', 'center_pleat'].includes(backStyle) && yokeEnabled && hasBackYokeDart;
  const canApplySidePleat = backStyle === 'side_pleat' && canApplyBackPleatShift;
  const canApplyCenterPleat = backStyle === 'center_pleat' && canApplyBackPleatShift;
  const sidePleatGuidePath = [CBY, backPanelDartMidpoint, backPanelDartBya];
  const sidePleatGuideLength = polylineLength(sidePleatGuidePath);
  const backSidePleatAutoOffsetFromBYDB = roundEighth(sidePleatGuideLength * 0.2);
  const backSidePleatOffsetFromBYDB = roundEighth(
    clamp(
      backSidePleatOffsetFromBYDBInput > 0 ? backSidePleatOffsetFromBYDBInput : backSidePleatAutoOffsetFromBYDB,
      0,
      sidePleatGuideLength,
    ),
  );

  const sidePleatShiftAtY = (y) => {
    if (!canApplyBackPleatShift || backPleatWidth <= 0) return 0;

    const yTop = backPanelDartBya.y;
    const yBottom = BWP.y;
    const span = Math.max(0.001, yBottom - yTop);

    if (y <= yTop) return backPleatWidth;
    if (y >= yBottom) return 0;

    const t = (y - yTop) / span;
    return backPleatWidth * (1 - t);
  };

  const shiftBackPleatPoint = (point) => rp(point.x + sidePleatShiftAtY(point.y), point.y);

  const backPanelDartMidpointResolved = canApplyBackPleatShift ? shiftBackPleatPoint(backPanelDartMidpoint) : backPanelDartMidpoint;
  const backPanelDartByaResolved = canApplyBackPleatShift ? shiftBackPleatPoint(backPanelDartBya) : backPanelDartBya;
  const backPanelDartControlResolved = canApplyBackPleatShift ? shiftBackPleatPoint(backPanelDartControl) : backPanelDartControl;
  let backPanelYokeJoinControlResolved = canApplyBackPleatShift ? shiftBackPleatPoint(backPanelYokeJoinControl) : backPanelYokeJoinControl;
  const BXBResolved = canApplyBackPleatShift ? shiftBackPleatPoint(BXB) : BXB;
  let backArmCtrl2Resolved = canApplyBackPleatShift ? shiftBackPleatPoint(backArmCtrl2) : backArmCtrl2;
  const BCPResolved = canApplyBackPleatShift ? shiftBackPleatPoint(BCP) : BCP;
  const backChestWaistCtrlResolved = canApplyBackPleatShift ? shiftBackPleatPoint(backChestWaistCtrl) : backChestWaistCtrl;

  if (hasBackYokeDart) {
    const smoothedBXBResolved = smoothQuadraticTurnPoint({
      prevPoint: backPanelDartByaResolved,
      turnPoint: BXBResolved,
      nextPoint: BCPResolved,
      incomingControl: backPanelYokeJoinControlResolved,
      outgoingControl: backArmCtrl2Resolved,
    });
    backPanelYokeJoinControlResolved = smoothedBXBResolved.incomingControl;
    backArmCtrl2Resolved = smoothedBXBResolved.outgoingControl;
  }

  const sidePleatAnchorInfo = getPointAndTangentOnPolyline(
    sidePleatGuidePath,
    Math.max(0, sidePleatGuideLength - backSidePleatOffsetFromBYDB),
  );
  const sidePleatAnchor = canApplySidePleat ? shiftBackPleatPoint(sidePleatAnchorInfo.point) : sidePleatAnchorInfo.point;
  if (canApplySidePleat) applyPointDeltaInPlace('SP1', sidePleatAnchor);
  const sidePleatFoldStart = sidePleatAnchor;
  const sidePleatFoldEnd = rp(
    sidePleatFoldStart.x,
    sidePleatFoldStart.y + backPleatLength,
  );
  const sidePleatSecondStart = rp(
    sidePleatFoldStart.x - backPleatWidth,
    sidePleatFoldStart.y,
  );
  if (canApplySidePleat) applyPointDeltaInPlace('SP2', sidePleatSecondStart);
  const sidePleatSecondEnd = rp(
    sidePleatSecondStart.x,
    sidePleatSecondStart.y + backPleatLength,
  );

  const centerPleatGuidePath = [CBY, backPanelDartMidpointResolved];
  const centerPleatGuideLength = polylineLength(centerPleatGuidePath);
  const centerPleatMarkerDistanceFromCBY = roundEighth(clamp(backPleatWidth, 0, centerPleatGuideLength));
  const centerPleatMarkerAnchor = canApplyCenterPleat
    ? getPointOnPolyline(centerPleatGuidePath, centerPleatMarkerDistanceFromCBY)
    : CBY;
  const centerPleatStart = rp(centerPleatMarkerAnchor.x, centerPleatMarkerAnchor.y + 0.125);
  const centerPleatEnd = rp(centerPleatStart.x, centerPleatStart.y + backPleatLength);

  const backPleatLines = backStyle === 'side_pleat' && canApplySidePleat
    ? [
      {
        id: 'back-side-pleat-fold',
        type: 'fold',
        x1: sidePleatFoldStart.x,
        y1: -sidePleatFoldStart.y,
        x2: sidePleatFoldEnd.x,
        y2: -sidePleatFoldEnd.y,
      },
      {
        id: 'back-side-pleat-fold-secondary',
        type: 'fold_secondary',
        x1: sidePleatSecondStart.x,
        y1: -sidePleatSecondStart.y,
        x2: sidePleatSecondEnd.x,
        y2: -sidePleatSecondEnd.y,
      },
    ]
    : backStyle === 'center_pleat' && canApplyCenterPleat
    ? [
      {
        id: 'back-center-pleat-marker',
        type: 'fold_secondary',
        x1: centerPleatStart.x,
        y1: -centerPleatStart.y,
        x2: centerPleatEnd.x,
        y2: -centerPleatEnd.y,
      },
    ]
    : [];

  const frontPanelArmCommands = segmentsToPathCommands(frontArmLowerSegments);
  const frontPanelNeckCommands = segmentsToPathCommands(frontNeckLowerSegments);

  const frontPathWithYoke = `
    M ${toSVG(frontNeckCut)}
    L ${toSVG(frontArmCut)}
    ${frontPanelArmCommands}
    Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FWP)}
    Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(FBP)}
    Q ${toSVG(frontBellySeatCtrl)} ${toSVG(FSP)}
    C ${toSVG(frontHemCtrl1)} ${toSVG(frontHemCtrl2)} ${toSVG(FHP)}
    L ${toSVG(CF7)}
    L ${toSVG(PL7)}
    L ${toSVG(PL1)}
    L ${toSVG(CF1)}
    ${frontPanelNeckCommands}
    Z
  `;

  const frontPathNoYoke = `
    M ${toSVG(CF1)}
    Q ${toSVG(frontCtrl)} ${toSVG(FHPS)}
    L ${toSVG(FLPS)}
    Q ${toSVG(frontArmCtrl1)} ${toSVG(FXF)}
    Q ${toSVG(frontArmCtrl2)} ${toSVG(FCP)}
    Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FWP)}
    Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(FBP)}
    Q ${toSVG(frontBellySeatCtrl)} ${toSVG(FSP)}
    C ${toSVG(frontHemCtrl1)} ${toSVG(frontHemCtrl2)} ${toSVG(FHP)}
    L ${toSVG(CF7)}
    L ${toSVG(PL7)}
    L ${toSVG(PL1)}
    L ${toSVG(CF1)}
    Z
  `;

  const frontPath = yokeEnabled ? frontPathWithYoke : frontPathNoYoke;

  const buttonExtensionPath = `M ${toSVG(E1)} L ${toSVG(E7)}`;
  const placketFoldPath = `M ${toSVG(PL1)} L ${toSVG(PL7)}`;

  const firstButtonY = CF1.y + firstButtonPosition;
  const lastButtonY = CF7.y - lastButtonPosition;
  const usableButtonSpan = Math.max(0, lastButtonY - firstButtonY);

  const pickAutoButtonCount = (span) => {
    if (span <= 0) return 1;

    const minSpacing = 3.25;
    const maxSpacing = 3.75;
    const targetSpacing = 3.5;
    const candidates = [];

    for (let count = 2; count <= 20; count += 1) {
      const spacing = span / (count - 1);
      if (spacing >= minSpacing && spacing <= maxSpacing) {
        candidates.push({ count, spacing, score: Math.abs(spacing - targetSpacing) });
      }
    }

    if (candidates.length > 0) {
      candidates.sort((a, b) => a.score - b.score);
      return candidates[0].count;
    }

    return Math.max(2, Math.round(span / targetSpacing) + 1);
  };

  const resolvedButtonCount = manualButtonCount >= 2
    ? manualButtonCount
    : pickAutoButtonCount(usableButtonSpan);

  const buttonSpacing = resolvedButtonCount > 1
    ? usableButtonSpan / (resolvedButtonCount - 1)
    : 0;

  const buttonCenters = Array.from({ length: resolvedButtonCount }, (_, idx) => {
    const yInternal = resolvedButtonCount > 1
      ? firstButtonY + buttonSpacing * idx
      : firstButtonY;
    return {
      id: idx + 1,
      x: CF1.x,
      y: yInternal,
    };
  });

  const buttonholes = buttonCenters.map((button, idx) => {
    const isLast = idx === buttonCenters.length - 1;
    const halfLength = 0.3125;
    if (isLast) {
      return {
        id: button.id,
        orientation: 'horizontal',
        start: { x: button.x - halfLength, y: button.y },
        end: { x: button.x + halfLength, y: button.y },
      };
    }

    return {
      id: button.id,
      orientation: 'vertical',
      start: { x: button.x, y: button.y - halfLength },
      end: { x: button.x, y: button.y + halfLength },
    };
  });

  const backPanelArmCommands = toLineCommands(backArmLowerPoints);

  const backPathWithYoke = hasBackYokeDart
    ? `
    M ${toSVG(CBY)}
    L ${toSVG(backPanelDartMidpointResolved)}
    Q ${toSVG(backPanelDartControlResolved)} ${toSVG(backPanelDartByaResolved)}
    Q ${toSVG(backPanelYokeJoinControlResolved)} ${toSVG(BXBResolved)}
    Q ${toSVG(backArmCtrl2Resolved)} ${toSVG(BCPResolved)}
    Q ${toSVG(backChestWaistCtrlResolved)} ${toSVG(BWP)}
    Q ${toSVG(backWaistBellyCtrl)} ${toSVG(BBP)}
    Q ${toSVG(backBellySeatCtrl)} ${toSVG(BSP)}
    C ${toSVG(backHemCtrl1)} ${toSVG(backHemCtrl2)} ${toSVG(BHP)}
    L ${toSVG(CB7)}
    L ${toSVG(CBY)}
    Z
  `
    : `
    M ${toSVG(CBY)}
    L ${toSVG(backArmYokeCut)}
    ${backPanelArmCommands}
    Q ${toSVG(backChestWaistCtrl)} ${toSVG(BWP)}
    Q ${toSVG(backWaistBellyCtrl)} ${toSVG(BBP)}
    Q ${toSVG(backBellySeatCtrl)} ${toSVG(BSP)}
    C ${toSVG(backHemCtrl1)} ${toSVG(backHemCtrl2)} ${toSVG(BHP)}
    L ${toSVG(CB7)}
    L ${toSVG(CBY)}
    Z
  `;

  const backPath = yokeEnabled ? backPathWithYoke : backBasePath;

  const backYokeArmCommands = toLineCommands(backArmUpperPoints);
  const yokeArmStartTangentPoint = hasBackYokeDart
    ? backYokeArmBezierControl
    : (backArmUpperPoints[1] || backArmUpperPoints[0] || backArmCtrl1);
  const backYokeShoulderJoinCommand = `L ${toSVG(BLPS)}`;
  const backYokePath = !yokeEnabled
    ? ''
    : hasBackYokeDart
    ? `
    M ${toSVG(CB1)}
    Q ${toSVG(backCtrl)} ${toSVG(BHPS)}
    ${backYokeShoulderJoinCommand}
    Q ${toSVG(backYokeArmBezierControl)} ${toSVG(yokeDartBya)}
    Q ${toSVG(yokeDartControl)} ${toSVG(yokeDartMidpoint)}
    L ${toSVG(CBY)}
    L ${toSVG(CB1)}
    Z
  `
    : `
    M ${toSVG(CB1)}
    Q ${toSVG(backCtrl)} ${toSVG(BHPS)}
    ${backYokeShoulderJoinCommand}
    ${backYokeArmCommands}
    L ${toSVG(CBY)}
    L ${toSVG(CB1)}
    Z
  `;

  const frontToBackShoulderVector = {
    x: BLPS.x - BHPS.x,
    y: BLPS.y - BHPS.y,
  };
  const frontShoulderSegment = {
    x: FLPS.x - FHPS.x,
    y: FLPS.y - FHPS.y,
  };
  const shoulderDot = frontShoulderSegment.x * frontToBackShoulderVector.x
    + frontShoulderSegment.y * frontToBackShoulderVector.y;
  const shoulderCross = frontShoulderSegment.x * frontToBackShoulderVector.y
    - frontShoulderSegment.y * frontToBackShoulderVector.x;
  const shoulderSignedDelta = Math.atan2(shoulderCross, shoulderDot);
  const clockwiseShoulderRotation = shoulderSignedDelta > 0
    ? shoulderSignedDelta - Math.PI * 2
    : shoulderSignedDelta;

  const shoulderTranslate = {
    x: BHPS.x - FHPS.x,
    y: BHPS.y - FHPS.y,
  };

  const mapFrontRolloverToYoke = (point) => {
    const rotated = rotateAroundAnchor(point, FHPS, clockwiseShoulderRotation);
    return rp(rotated.x + shoulderTranslate.x, rotated.y + shoulderTranslate.y);
  };

  const mapSegmentToYoke = (segment) => (
    segment?.type === 'C' || segment?.p3
      ? {
          type: 'C',
          p0: mapFrontRolloverToYoke(segment.p0),
          p1: mapFrontRolloverToYoke(segment.p1),
          p2: mapFrontRolloverToYoke(segment.p2),
          p3: mapFrontRolloverToYoke(segment.p3),
        }
      : {
          p0: mapFrontRolloverToYoke(segment.p0),
          p1: mapFrontRolloverToYoke(segment.p1),
          p2: mapFrontRolloverToYoke(segment.p2),
        }
  );

  const frontNeckUpperJoinedSegments = frontNeckUpperSegments.map(mapSegmentToYoke);
  const frontArmUpperJoinedSegments = frontArmUpperSegments.map(mapSegmentToYoke);
  const frontNeckCutJoined = mapFrontRolloverToYoke(frontNeckCut);
  const frontLpsJoined = mapFrontRolloverToYoke(FLPS);

  const frontYokeNeckCommands = segmentsToPathCommands(frontNeckUpperJoinedSegments);
  const frontYokeArmCommands = segmentsToPathCommands(frontArmUpperJoinedSegments);

  const frontYokePath = `
    M ${toSVG(frontNeckCutJoined)}
    ${frontYokeNeckCommands}
    L ${toSVG(frontLpsJoined)}
    ${frontYokeArmCommands}
    L ${toSVG(frontNeckCutJoined)}
    Z
  `;

  const yokePath = yokeEnabled
    ? `${backYokePath}\n${frontYokePath}`
    : '';

  const yokeDartPath = '';

  const dartPath = fishDartEnabled ? `
    M ${toSVG(BD1)} L ${toSVG(BD0)} L ${toSVG(BD2)}
    M ${toSVG(BD3)} L ${toSVG(BD5)} L ${toSVG(BD4)}
    M ${toSVG(BD1)} L ${toSVG(BD3)}
    M ${toSVG(BD2)} L ${toSVG(BD4)}
  ` : '';

  const constructionLinesRaw = [
    { p1: CF3, p2: FCP, label: 'Front Chest' },
    { p1: CF4, p2: FWP, label: 'Front Waist' },
    { p1: CF5, p2: FBP, label: 'Front Belly' },
    { p1: CB3, p2: BCPResolved, label: 'Back Chest' },
    { p1: CB4, p2: BWP, label: 'Back Waist' },
    { p1: CB5, p2: BBP, label: 'Back Belly' },
  ];

  const frontWaistLine = dist(CF4, FWP);
  const frontBellyLine = dist(CF5, FBP);
  const backWaistRawLine = dist(CB4, BWP);
  const backBellyRawLine = dist(CB5, BBP);
  const frontSeatLine = dist(CF6, FSP);
  const backSeatLine = dist(CB6, BSP);
  const acrossFrontLine = dist(FXF, CF2);
  const acrossBackLine = dist(BXBResolved, CB2);
  const shoulderHorizontalLine = dist(BLPS, shoulderBackToCb);
  const frontScyeDepth = dist(FLPS, frontScyeBottom);
  const backScyeDepth = dist(BLPS, backScyeBottom);
  const avgScyeDepth = (frontScyeDepth + backScyeDepth) / 2;
  const totalSeatHalf = frontSeatLine + backSeatLine;
  const waistDartLine = dist(BD1, BD2);
  const bellyDartLine = dist(BD3, BD4);
  const backWaistNetLine = roundEighth(backWaistRawLine - waistDartLine);
  const backBellyNetLine = roundEighth(backBellyRawLine - bellyDartLine);
  const totalWaistHalf = roundEighth(frontWaistLine + backWaistNetLine);
  const totalBellyHalf = roundEighth(frontBellyLine + backBellyNetLine);
  const totalWaistRawHalf = roundEighth(frontWaistLine + backWaistRawLine);
  const totalBellyRawHalf = roundEighth(frontBellyLine + backBellyRawLine);
  const totalNeckHalf = backNeckLenAdjusted + frontNeckLenAdjusted;
  const backArmholeHalfRaw = quadLength(BLPS, backArmCtrl1, BXBResolved) + quadLength(BXBResolved, backArmCtrl2Resolved, BCPResolved);
  const frontArmholeHalf = quadLength(FLPS, frontArmCtrl1, FXF) + quadLength(FXF, frontArmCtrl2, FCP);
  const armholeDartReductionHalf = hasBackYokeDart ? yokeDartWidth : 0;
  const backArmholeHalf = Math.max(0, backArmholeHalfRaw - armholeDartReductionHalf);
  const totalArmholeHalf = backArmholeHalf + frontArmholeHalf;
  const totalChestHalf = dist(CF3, FCP) + dist(CB3, BCPResolved);
  const shirtLengthBackDrawn = Math.abs(CB7.y - BHPS.y);
  const shirtLengthFrontDrawn = Math.abs(CF7.y - FHPS.y);

  const sleeveDraftLength = roundSixteenth(Math.max(4, sleeveLength - (isShortSleeve ? 0 : sleeveCuffHeight)));
  const sleeveDisplayedLength = roundSixteenth(isShortSleeve ? sleeveLength : (sleeveDraftLength + sleeveCuffHeight));
  const sleeveTargetCapPath = Math.max(4, totalArmholeHalf + 0.125);
  const sleeveOffsetX = roundSixteenth(
    Math.max(PL7.x, CF7.x, xCF) + 10 + Math.max(0, (bicepWidth - 14) * 0.08),
  );

  const buildSleeveGeometry = (capHeightInput, applyFreePointDeltas = false) => {
    const capHeight = roundSixteenth(clamp(capHeightInput, 2.5, Math.max(3, sleeveDraftLength - 1.5)));
    const distToElbowRawAuto = isShortSleeve
      ? capHeight + ((sleeveDraftLength - capHeight) * 0.55)
      : capHeight + ((sleeveDraftLength - capHeight) * 0.5) - 1;
    const distToElbowRaw = hasSleeveDistToElbowOverride ? sleeveDistToElbowInput : distToElbowRawAuto;
    const distToElbow = roundSixteenth(clamp(
      distToElbowRaw,
      capHeight + (isShortSleeve ? 0.5 : 0.75),
      sleeveDraftLength - (isShortSleeve ? 0.5 : 1),
    ));

    const SL1 = rp16(0, 0);
    const BiC = rp16(0, capHeight);
    const ElC = rp16(0, distToElbow);
    const HC = rp16(0, sleeveDraftLength);

    const closedBicepWidth = bicepWidth / 2;
    const closedElbowWidth = elbowWidth / 2;
    const closedHemWidth = isShortSleeve
      ? maxCuffWidth / 2
      : (maxCuffWidth + sleevePleatAllowance) / 2;

    const Bi1 = rp16(-closedBicepWidth / 2, BiC.y);
    const Bi2 = rp16(closedBicepWidth / 2, BiC.y);
    const EL1 = rp16(-closedElbowWidth / 2, ElC.y);
    const EL2 = rp16(closedElbowWidth / 2, ElC.y);
    const sleeveHemHalfWidth = closedHemWidth / 2;
    const H1 = rp16(-sleeveHemHalfWidth, HC.y);
    const H2 = rp16(sleeveHemHalfWidth, HC.y);

    const FP = rp16(Bi1.x, capHeight * sleeveFrontPitchPercent);
    const BP = rp16(Bi2.x, capHeight * sleeveBackPitchPercent);

    const BiC1 = reflectPointAcrossLine(BiC, FP, Bi1);
    const BiC2 = reflectPointAcrossLine(BiC, BP, Bi2);
    const ELC1 = reflectPointAcrossLine(ElC, Bi1, EL1);
    const ELC2 = reflectPointAcrossLine(ElC, Bi2, EL2);
    const HC1 = reflectPointAcrossLine(HC, EL1, H1);
    const HC2 = reflectPointAcrossLine(HC, EL2, H2);

    const sleeveFrontUnderarmControl = rp16(
      FP.x + sleeveFrontUnderarmCtrlDx,
      FP.y + Math.max(0.35, (BiC1.y - FP.y) * 0.72) + sleeveFrontUnderarmCtrlDy,
    );

    const sleeveFrontCrownControl = rp16(
      FP.x + sleeveFrontCrownCtrlDx,
      SL1.y + sleeveFrontCrownCtrlDy,
    );

    const sleeveBackCrownControl = rp16(
      BP.x + sleeveBackCrownCtrlDx,
      SL1.y + sleeveBackCrownCtrlDy,
    );

    const sleeveBackUnderarmControl = rp16(
      BP.x + sleeveBackUnderarmCtrlDx,
      BP.y + Math.max(0.35, (BiC2.y - BP.y) * 0.68) + sleeveBackUnderarmCtrlDy,
    );

    const sleeveFrontSideUpperTarget = isShortSleeve ? HC1 : ELC1;
    const sleeveBackSideUpperTarget = isShortSleeve ? HC2 : ELC2;

    const sleeveFrontSideUpperControl = rp16(
      (BiC1.x + sleeveFrontSideUpperTarget.x) / 2 + sleeveFrontSideUpperCtrlDx,
      (BiC1.y + sleeveFrontSideUpperTarget.y) / 2 + sleeveFrontSideUpperCtrlDy,
    );

    const sleeveFrontSideLowerControl = rp16(
      (ELC1.x + HC1.x) / 2 + sleeveFrontSideLowerCtrlDx,
      (ELC1.y + HC1.y) / 2 + sleeveFrontSideLowerCtrlDy,
    );

    const sleeveBackSideUpperControl = rp16(
      (BiC2.x + sleeveBackSideUpperTarget.x) / 2 + sleeveBackSideUpperCtrlDx,
      (BiC2.y + sleeveBackSideUpperTarget.y) / 2 + sleeveBackSideUpperCtrlDy,
    );

    const sleeveBackSideLowerControl = rp16(
      (ELC2.x + HC2.x) / 2 + sleeveBackSideLowerCtrlDx,
      (ELC2.y + HC2.y) / 2 + sleeveBackSideLowerCtrlDy,
    );

    const sleevePL1 = isShortSleeve ? rp16(H2.x, H2.y) : rp16(H2.x, H2.y - sleevePlacketHeight);

    const shift = (point) => rp16(point.x + sleeveOffsetX, point.y);

    const shifted = {
      SL1: shift(SL1),
      BiC: shift(BiC),
      ElC: shift(ElC),
      HC: shift(HC),
      Bi1: shift(Bi1),
      Bi2: shift(Bi2),
      EL1: shift(EL1),
      EL2: shift(EL2),
      H1: shift(H1),
      H2: shift(H2),
      FP: shift(FP),
      BP: shift(BP),
      BiC1: shift(BiC1),
      BiC2: shift(BiC2),
      ELC1: shift(ELC1),
      ELC2: shift(ELC2),
      HC1: shift(HC1),
      HC2: shift(HC2),
      sleevePL1: shift(sleevePL1),
      sleeveFrontUnderarmControl: shift(sleeveFrontUnderarmControl),
      sleeveFrontCrownControl: shift(sleeveFrontCrownControl),
      sleeveBackCrownControl: shift(sleeveBackCrownControl),
      sleeveBackUnderarmControl: shift(sleeveBackUnderarmControl),
      sleeveFrontSideUpperControl: shift(sleeveFrontSideUpperControl),
      sleeveFrontSideLowerControl: shift(sleeveFrontSideLowerControl),
      sleeveBackSideUpperControl: shift(sleeveBackSideUpperControl),
      sleeveBackSideLowerControl: shift(sleeveBackSideLowerControl),
    };

    if (applyFreePointDeltas) {
      [
        ['SL1', shifted.SL1],
        ['BiC', shifted.BiC],
        ['ElC', shifted.ElC],
        ['HC', shifted.HC],
        ['Bi1', shifted.Bi1],
        ['Bi2', shifted.Bi2],
        ['EL1', shifted.EL1],
        ['EL2', shifted.EL2],
        ['H1', shifted.H1],
        ['H2', shifted.H2],
        ['FP', shifted.FP],
        ['BP', shifted.BP],
        ['BiC1', shifted.BiC1],
        ['BiC2', shifted.BiC2],
        ['ELC1', shifted.ELC1],
        ['ELC2', shifted.ELC2],
        ['HC1', shifted.HC1],
        ['HC2', shifted.HC2],
        ['PL1S', shifted.sleevePL1],
      ].forEach(([pointName, point]) => applyPointDeltaInPlace(pointName, point));
    }

    const capFrontUnderarmLength = quadLength(shifted.BiC1, shifted.sleeveFrontUnderarmControl, shifted.FP, 72);
    const capFrontCrownLength = quadLength(shifted.FP, shifted.sleeveFrontCrownControl, shifted.SL1, 72);
    const capBackCrownLength = quadLength(shifted.SL1, shifted.sleeveBackCrownControl, shifted.BP, 72);
    const capBackUnderarmLength = quadLength(shifted.BP, shifted.sleeveBackUnderarmControl, shifted.BiC2, 72);
    const capPathLength = capFrontUnderarmLength + capFrontCrownLength + capBackCrownLength + capBackUnderarmLength;
    const sleeveFrontSeamLength = isShortSleeve
      ? quadLength(shifted.BiC1, shifted.sleeveFrontSideUpperControl, shifted.HC1, 72)
      : quadLength(shifted.BiC1, shifted.sleeveFrontSideUpperControl, shifted.ELC1, 72)
        + quadLength(shifted.ELC1, shifted.sleeveFrontSideLowerControl, shifted.HC1, 72);
    const sleeveBackSeamLength = isShortSleeve
      ? quadLength(shifted.BiC2, shifted.sleeveBackSideUpperControl, shifted.HC2, 72)
      : quadLength(shifted.BiC2, shifted.sleeveBackSideUpperControl, shifted.ELC2, 72)
        + quadLength(shifted.ELC2, shifted.sleeveBackSideLowerControl, shifted.HC2, 72);

    const wrappedFrontMirrorPoint = reflectPointAcrossLine(shifted.BiC1, shifted.FP, shifted.Bi1);
    const wrappedFrontMirrorControl = reflectPointAcrossLine(
      shifted.sleeveFrontUnderarmControl,
      shifted.FP,
      shifted.Bi1,
    );
    const wrappedBackMirrorPoint = reflectPointAcrossLine(shifted.BiC2, shifted.BP, shifted.Bi2);
    const wrappedBackMirrorControl = reflectPointAcrossLine(
      shifted.sleeveBackUnderarmControl,
      shifted.BP,
      shifted.Bi2,
    );

    const sleevePath = isShortSleeve
      ? `
    M ${toSVG(shifted.BiC1)}
    Q ${toSVG(shifted.sleeveFrontUnderarmControl)} ${toSVG(shifted.FP)}
    Q ${toSVG(shifted.sleeveFrontCrownControl)} ${toSVG(shifted.SL1)}
    Q ${toSVG(shifted.sleeveBackCrownControl)} ${toSVG(shifted.BP)}
    Q ${toSVG(shifted.sleeveBackUnderarmControl)} ${toSVG(shifted.BiC2)}
    Q ${toSVG(shifted.sleeveBackSideUpperControl)} ${toSVG(shifted.HC2)}
    L ${toSVG(shifted.H2)}
    L ${toSVG(shifted.HC)}
    L ${toSVG(shifted.H1)}
    L ${toSVG(shifted.HC1)}
    Q ${toSVG(shifted.sleeveFrontSideUpperControl)} ${toSVG(shifted.BiC1)}
    Z
  `
      : `
    M ${toSVG(shifted.BiC1)}
    Q ${toSVG(shifted.sleeveFrontUnderarmControl)} ${toSVG(shifted.FP)}
    Q ${toSVG(shifted.sleeveFrontCrownControl)} ${toSVG(shifted.SL1)}
    Q ${toSVG(shifted.sleeveBackCrownControl)} ${toSVG(shifted.BP)}
    Q ${toSVG(shifted.sleeveBackUnderarmControl)} ${toSVG(shifted.BiC2)}
    Q ${toSVG(shifted.sleeveBackSideUpperControl)} ${toSVG(shifted.ELC2)}
    Q ${toSVG(shifted.sleeveBackSideLowerControl)} ${toSVG(shifted.HC2)}
    L ${toSVG(shifted.H2)}
    L ${toSVG(shifted.HC)}
    L ${toSVG(shifted.H1)}
    L ${toSVG(shifted.HC1)}
    Q ${toSVG(shifted.sleeveFrontSideLowerControl)} ${toSVG(shifted.ELC1)}
    Q ${toSVG(shifted.sleeveFrontSideUpperControl)} ${toSVG(shifted.BiC1)}
    Z
  `;

    const sleeveLengthPath = `M ${toSVG(shifted.SL1)} L ${toSVG(shifted.HC)}`;
    const sleevePlacketPath = isShortSleeve ? '' : `M ${toSVG(shifted.H2)} L ${toSVG(shifted.sleevePL1)}`;
    const sleeveWrappedMirrorPath = `
    M ${toSVG(wrappedFrontMirrorPoint)} Q ${toSVG(wrappedFrontMirrorControl)} ${toSVG(shifted.FP)}
    M ${toSVG(shifted.BP)} Q ${toSVG(wrappedBackMirrorControl)} ${toSVG(wrappedBackMirrorPoint)}
  `;

    return {
      capHeight,
      distToElbow,
      capPathLength,
      capFrontUnderarmLength,
      capFrontCrownLength,
      capBackCrownLength,
      capBackUnderarmLength,
      sleeveFrontSeamLength,
      sleeveBackSeamLength,
      path: sleevePath,
      sleeveLengthPath,
      sleevePlacketPath,
      sleeveWrappedMirrorPath,
      points: shifted,
      wrappedMirror: {
        wrappedFrontMirrorPoint,
        wrappedFrontMirrorControl,
        wrappedBackMirrorPoint,
        wrappedBackMirrorControl,
      },
      controls: {
        sleeveFrontUnderarmControl: shifted.sleeveFrontUnderarmControl,
        sleeveFrontCrownControl: shifted.sleeveFrontCrownControl,
        sleeveBackCrownControl: shifted.sleeveBackCrownControl,
        sleeveBackUnderarmControl: shifted.sleeveBackUnderarmControl,
        sleeveFrontSideUpperControl: shifted.sleeveFrontSideUpperControl,
        sleeveFrontSideLowerControl: shifted.sleeveFrontSideLowerControl,
        sleeveBackSideUpperControl: shifted.sleeveBackSideUpperControl,
        sleeveBackSideLowerControl: shifted.sleeveBackSideLowerControl,
      },
      constructionLines: [
        { p1: toInternalPoint(shifted.Bi1), p2: toInternalPoint(shifted.Bi2), label: 'Sleeve Bicep' },
        { p1: toInternalPoint(shifted.BiC1), p2: toInternalPoint(shifted.Bi1), label: 'Front Wrap Fold' },
        { p1: toInternalPoint(shifted.Bi2), p2: toInternalPoint(shifted.BiC2), label: 'Back Wrap Fold' },
        ...(!isShortSleeve ? [
          { p1: toInternalPoint(shifted.EL1), p2: toInternalPoint(shifted.EL2), label: 'Sleeve Elbow' },
          { p1: toInternalPoint(shifted.ELC1), p2: toInternalPoint(shifted.EL1), label: 'Front Elbow Fold' },
          { p1: toInternalPoint(shifted.EL2), p2: toInternalPoint(shifted.ELC2), label: 'Back Elbow Fold' },
        ] : []),
        { p1: toInternalPoint(shifted.H1), p2: toInternalPoint(shifted.H2), label: 'Sleeve Hem' },
        { p1: toInternalPoint(shifted.SL1), p2: toInternalPoint(shifted.HC), label: 'Sleeve Length' },
        ...(!isShortSleeve ? [{ p1: toInternalPoint(shifted.H2), p2: toInternalPoint(shifted.sleevePL1), label: 'Sleeve Placket' }] : []),
      ],
    };
  };

  const minCapHeight = 2.5;
  const maxCapHeight = Math.max(minCapHeight + 0.5, sleeveDraftLength - 1.5);

  let loCap = minCapHeight;
  let hiCap = maxCapHeight;
  let loGeom = buildSleeveGeometry(loCap);
  let hiGeom = buildSleeveGeometry(hiCap);
  let bestSleeve = Math.abs(loGeom.capPathLength - sleeveTargetCapPath) < Math.abs(hiGeom.capPathLength - sleeveTargetCapPath)
    ? loGeom
    : hiGeom;

  let loErr = loGeom.capPathLength - sleeveTargetCapPath;
  let hiErr = hiGeom.capPathLength - sleeveTargetCapPath;

  if (loErr * hiErr <= 0) {
    for (let i = 0; i < 28; i += 1) {
      const mid = (loCap + hiCap) / 2;
      const midGeom = buildSleeveGeometry(mid);
      const midErr = midGeom.capPathLength - sleeveTargetCapPath;

      if (Math.abs(midErr) < Math.abs(bestSleeve.capPathLength - sleeveTargetCapPath)) {
        bestSleeve = midGeom;
      }

      if (Math.abs(midErr) <= 0.015625) {
        bestSleeve = midGeom;
        break;
      }

      if ((loErr <= 0 && midErr <= 0) || (loErr >= 0 && midErr >= 0)) {
        loCap = mid;
        loErr = midErr;
      } else {
        hiCap = mid;
        hiErr = midErr;
      }
    }
  } else {
    for (let i = 0; i < 32; i += 1) {
      const t = i / 31;
      const sampleCap = loCap + (hiCap - loCap) * t;
      const sampleGeom = buildSleeveGeometry(sampleCap);
      if (Math.abs(sampleGeom.capPathLength - sleeveTargetCapPath) < Math.abs(bestSleeve.capPathLength - sleeveTargetCapPath)) {
        bestSleeve = sampleGeom;
      }
    }
  }

  if (freeModeActive && Object.keys(freePointDeltas).length > 0) {
    bestSleeve = buildSleeveGeometry(bestSleeve.capHeight, true);
  }

  const sleevePath = bestSleeve.path;
  const sleeveLengthPath = bestSleeve.sleeveLengthPath;
  const sleevePlacketPath = bestSleeve.sleevePlacketPath;
  const sleeveWrappedMirrorPath = bestSleeve.sleeveWrappedMirrorPath;
  const sleeveConstructionLines = bestSleeve.constructionLines;
  const sleeveSeamDelta = Math.abs(bestSleeve.sleeveFrontSeamLength - bestSleeve.sleeveBackSeamLength);
  const sleeveBicepUnwrappedDrawn = dist(bestSleeve.points.BiC1, bestSleeve.points.BiC2);
  const sleeveElbowUnwrappedDrawn = dist(bestSleeve.points.ELC1, bestSleeve.points.ELC2);
  const sleeveHemUnwrappedDrawn = dist(bestSleeve.points.HC1, bestSleeve.points.HC2);

  const sleeveCapPolyline = [
    ...sampleQuadratic(bestSleeve.points.BiC1, bestSleeve.controls.sleeveFrontUnderarmControl, bestSleeve.points.FP, 84),
    ...sampleQuadratic(bestSleeve.points.FP, bestSleeve.controls.sleeveFrontCrownControl, bestSleeve.points.SL1, 84).slice(1),
    ...sampleQuadratic(bestSleeve.points.SL1, bestSleeve.controls.sleeveBackCrownControl, bestSleeve.points.BP, 84).slice(1),
    ...sampleQuadratic(bestSleeve.points.BP, bestSleeve.controls.sleeveBackUnderarmControl, bestSleeve.points.BiC2, 84).slice(1),
  ];

  const sleeveDistanceToSL1 = bestSleeve.capFrontUnderarmLength + bestSleeve.capFrontCrownLength;

  const sleeveNotchSL1 = getPointAndTangentOnPolyline(sleeveCapPolyline, sleeveDistanceToSL1);
  const sleeveNotchSL1Internal = {
    point: rp16(sleeveNotchSL1.point.x, -sleeveNotchSL1.point.y),
    tangent: normalizeOrFallback({ x: sleeveNotchSL1.tangent.x, y: -sleeveNotchSL1.tangent.y }),
  };

  const frontArmholePolyline = [
    ...sampleQuadratic(FLPS, frontArmCtrl1, FXF, 84),
    ...sampleQuadratic(FXF, frontArmCtrl2, FCP, 84).slice(1),
  ];

  const backArmholePolyline = [
    ...sampleQuadratic(BLPS, backArmCtrl1, BXBResolved, 84),
    ...sampleQuadratic(BXBResolved, backArmCtrl2Resolved, BCPResolved, 84).slice(1),
  ];

  const frontArmholeLength = polylineLength(frontArmholePolyline);
  const backArmholeLength = polylineLength(backArmholePolyline);
  const sleeveToFrontUnderarmDistance = bestSleeve.capFrontCrownLength + bestSleeve.capFrontUnderarmLength;

  const bodyUnderarmOnFront = sleeveToFrontUnderarmDistance <= frontArmholeLength;
  const bodyUnderarmNotch = bodyUnderarmOnFront
    ? getPointAndTangentOnPolyline(frontArmholePolyline, sleeveToFrontUnderarmDistance)
    : getPointAndTangentOnPolyline(backArmholePolyline, sleeveToFrontUnderarmDistance - frontArmholeLength);
  const bodyShoulderNotch = getPointAndTangentOnPolyline(backArmholePolyline, 0);
  const shoulderNotchPiece = yokeEnabled ? 'yoke' : 'back';
  const bodyUnderarmPiece = bodyUnderarmOnFront ? 'front' : 'back';
  const bodyUnderarmInwardHint = bodyUnderarmOnFront
    ? rp16((CF4.x + FWP.x) / 2, (CF4.y + FWP.y) / 2)
    : rp16((CB4.x + BWP.x) / 2, (CB4.y + BWP.y) / 2);

  const notchMarks = [
    {
      id: 'notch-sleeve-sl1-single-t',
      piece: 'sleeve',
      role: 'shoulder_anchor',
      type: 'single_t',
      paths: buildNotchSymbolPaths(sleeveNotchSL1Internal.point, sleeveNotchSL1Internal.tangent, 'single_t', {
        size: 0.18,
        depth: 0.24,
      }),
    },
    {
      id: 'notch-body-shoulder-junction-single-t',
      piece: shoulderNotchPiece,
      role: 'shoulder_junction',
      type: 'single_t',
      paths: buildNotchSymbolPaths(bodyShoulderNotch.point, bodyShoulderNotch.tangent, 'single_t', {
        size: 0.2,
        depth: 0.28,
      }),
    },
    {
      id: 'notch-body-underarm-single-t',
      piece: bodyUnderarmPiece,
      role: 'underarm',
      type: 'single_t',
      paths: buildNotchSymbolPaths(bodyUnderarmNotch.point, bodyUnderarmNotch.tangent, 'single_t', {
        size: 0.2,
        depth: 0.28,
        inwardToward: bodyUnderarmInwardHint,
      }),
    },
  ];

  const frontMeasurementsRaw = [
    { label: 'FC', p1: CF3, p2: FCP, value: dist(CF3, FCP).toFixed(2), referenceLine: true },
    { label: 'AF', p1: FXF, p2: CF2, value: acrossFrontLine.toFixed(2), offset: 0.35, referenceLine: true },
    { label: 'FW', p1: CF4, p2: FWP, value: frontWaistLine.toFixed(2), referenceLine: true },
    { label: 'FB', p1: CF5, p2: FBP, value: frontBellyLine.toFixed(2), referenceLine: true },
    { label: 'FS', p1: CF6, p2: FSP, value: frontSeatLine.toFixed(2), referenceLine: true },
  ];

  const backMeasurementsRaw = [
    { label: 'BC', p1: CB3, p2: BCPResolved, value: dist(CB3, BCPResolved).toFixed(2), referenceLine: true },
    { label: 'AB', p1: BXBResolved, p2: CB2, value: acrossBackLine.toFixed(2), offset: 0.35, referenceLine: true },
    { label: 'SH', p1: BLPS, p2: shoulderBackToCb, value: shoulderHorizontalLine.toFixed(2), offset: 0.35, referenceLine: true },
    { label: 'BW', p1: CB4, p2: BWP, value: backWaistNetLine.toFixed(2), referenceLine: true },
    { label: 'BB', p1: CB5, p2: BBP, value: backBellyNetLine.toFixed(2), referenceLine: true },
    { label: 'BS', p1: CB6, p2: BSP, value: backSeatLine.toFixed(2), referenceLine: true },
  ];

  const frontPoints = {
    CF0,
    CF1,
    CF2,
    CF3,
    CF4,
    CF5,
    CF6,
    CF7,
    FHPS,
    FLPS,
    FXF,
    FCP,
    FWP,
    FBP,
    FSP,
    FHP,
    FNY: frontNeckCut,
    FAY: frontArmCut,
    E1,
    E7,
    PL1,
    PL7,
  };

  const backPoints = {
    CB0,
    CB1,
    CB2,
    CB3,
    CB4,
    CB5,
    CB6,
    CB7,
    BHPS,
    BLPS,
    BXB: BXBResolved,
    BCP: BCPResolved,
    BWP,
    BBP,
    BSP,
    BHP,
    BD0,
    BD1,
    BD2,
    BD3,
    BD4,
    BD5,
    CBY,
    ...(!hasBackYokeDart ? {
      BYA: backArmYokeCut,
    } : {}),
    ...(hasBackYokeDart ? {
      BYM: yokeDartMidpoint,
      BYDY: yokeDartBya,
      BYDB: backPanelDartByaResolved,
    } : {}),
    ...(canApplySidePleat ? {
      SP1: sidePleatAnchor,
      SP2: sidePleatSecondStart,
    } : {}),
  };

  const sleevePoints = {
    SL1: bestSleeve.points.SL1,
    BiC: bestSleeve.points.BiC,
    ElC: bestSleeve.points.ElC,
    HC: bestSleeve.points.HC,
    Bi1: bestSleeve.points.Bi1,
    Bi2: bestSleeve.points.Bi2,
    EL1: bestSleeve.points.EL1,
    EL2: bestSleeve.points.EL2,
    H1: bestSleeve.points.H1,
    H2: bestSleeve.points.H2,
    FP: bestSleeve.points.FP,
    BP: bestSleeve.points.BP,
    BiC1: bestSleeve.points.BiC1,
    BiC2: bestSleeve.points.BiC2,
    ELC1: bestSleeve.points.ELC1,
    ELC2: bestSleeve.points.ELC2,
    HC1: bestSleeve.points.HC1,
    HC2: bestSleeve.points.HC2,
    PL1S: bestSleeve.points.sleevePL1,
  };

  const collectSegmentPoints = (segments = []) => segments.flatMap((segment, index) => (
    index === 0 ? [segment.p0, segment.p1, segment.p2] : [segment.p1, segment.p2]
  ));

  const frontNeckUpperJoinedPoints = collectSegmentPoints(frontNeckUpperJoinedSegments);
  const frontArmUpperJoinedPoints = collectSegmentPoints(frontArmUpperJoinedSegments);

  const allPointsInternal = [
    ...Object.values(frontPoints),
    ...Object.values(backPoints),
    ...Object.values(sleevePoints),
    backCtrl,
    frontCtrl,
    backArmCtrl1,
    backArmCtrl2,
    frontArmCtrl1,
    frontArmCtrl2,
    frontChestWaistCtrl,
    frontWaistBellyCtrl,
    frontBellySeatCtrl,
    backChestWaistCtrl,
    backWaistBellyCtrl,
    backBellySeatCtrl,
    backHemCtrl1,
    backHemCtrl2,
    frontHemCtrl1,
    frontHemCtrl2,
    ...frontNeckUpperJoinedPoints,
    ...frontArmUpperJoinedPoints,
    frontNeckCutJoined,
    frontLpsJoined,
    bestSleeve.controls.sleeveFrontUnderarmControl,
    bestSleeve.controls.sleeveFrontCrownControl,
    bestSleeve.controls.sleeveBackCrownControl,
    bestSleeve.controls.sleeveBackUnderarmControl,
    bestSleeve.wrappedMirror.wrappedFrontMirrorPoint,
    bestSleeve.wrappedMirror.wrappedFrontMirrorControl,
    bestSleeve.wrappedMirror.wrappedBackMirrorPoint,
    bestSleeve.wrappedMirror.wrappedBackMirrorControl,
    yokeDartControl,
    backPanelDartControlResolved,
    ...(hasBackYokeDart ? [backYokeArmBezierControl, backPanelYokeJoinControlResolved] : []),
  ].map(toInternalPoint);
  const viewBox = createViewBox(allPointsInternal);

  const constructionLines = constructionLinesRaw.map((line) => ({
    ...line,
    p1: toInternalPoint(line.p1),
    p2: toInternalPoint(line.p2),
  }));

  const frontMeasurements = frontMeasurementsRaw.map((measurement) => ({
    ...measurement,
    p1: toInternalPoint(measurement.p1),
    p2: toInternalPoint(measurement.p2),
  }));

  const backMeasurements = backMeasurementsRaw.map((measurement) => ({
    ...measurement,
    p1: toInternalPoint(measurement.p1),
    p2: toInternalPoint(measurement.p2),
  }));

  const pointReferences = [
    'Origin system: CB0 is absolute origin (0,0); +X toward CF and +Y toward hem.',
    'Rounding rule: every derived coordinate is rounded to nearest 0.125 inch.',
    `Back neck width auto = Neck / 5.75 = ${autoBackNeckWidth.toFixed(3)} in (rounded).`,
    `Front neck width auto = Neck / 5.75 = ${autoFrontNeckWidth.toFixed(3)} in (rounded).`,
    `Front neck depth auto start = front neck width + 0.5 = ${autoFrontNeckDepth.toFixed(3)} in.`,
    'Chest level from shoulder strap uses scye average rule: avg((BLPS→Chest), (FLPS→Chest)) = Armhole Scye Depth.',
    `Across-back control: CB2→BXB = Across Back / 2 = ${(acrossBack / 2).toFixed(3)} in (rounded).`,
    `Across-front control: CF2→FXF = Across Front / 2 = ${(acrossFront / 2).toFixed(3)} in (rounded).`,
    'Portly shift logic: back widths = body/4 - portly/2, front widths = body/4 + portly/2.',
    'Dart centerline is BD0→BD5; BD1/BD2 and BD3/BD4 are kept horizontally equidistant from this centerline.',
    'Dart suppression is drafted separately and is not included in width split calculations.',
    'Waist fitting loop: move FWP and BWP outward/inward together in 0.125 in steps until net waist reaches target.',
    'Belly fitting loop: move FBP and BBP outward/inward together in 0.125 in steps until net belly reaches target.',
    'Total waist width check: (CF4→FWP) + ((CB4→BWP) - (BD1→BD2)).',
    'Total belly width check: (CF5→FBP) + ((CB5→BBP) - (BD3→BD4)).',
    'Curve style follows trouser method: quadratic Bézier segments with control points derived from nearby block measurements.',
    'Back armhole is drafted as two curves BLPS→BXB→BCP and passes through BXB exactly.',
    'Front armhole is drafted as two curves FLPS→FXF→FCP and passes through FXF exactly.',
    'Front lower armhole segment is FXF→FCP with control point on chest line for tangency at FCP.',
    'Back lower armhole segment BXB→BCP mirrors front lower-segment tension about BCP.',
    'Front neck control default is solved with 90° start tangent at CF1 and shoulder-joined smoothness target at FHPS.',
    decoupleArmholeSplitControls
      ? 'Armhole split controls are decoupled at BXB/FXF: editing one split segment control does not auto-adjust the neighboring split segment control.'
      : 'Armhole join continuity defaults to G1 (180°) at BXB and FXF by collinear handle generation across split segments.',
    'Back neck default enforces 90° tangent at CB1; front neck default enforces 90° tangent at CF1 with shoulder-join smoothing at HPS.',
    'Hem anchors: BHP and FHP sit horizontally from CB7/CF7 at 25% of CB6→BSP and CF7→FSP respectively.',
    'Hem curves are drafted as BSP↔BHP and FSP↔FHP cubic segments; BHP→CB7 and FHP→CF7 are straight horizontal links.',
    `Center front is drafting reference only (CF1→CF7 hidden). Button extension line E is ${buttonExtensionWidth.toFixed(3)} in from CF, placket fold line PL is additional ${placketFoldWidth.toFixed(3)} in from E.`,
    `Button plan: first = ${firstButtonPosition.toFixed(3)} in below CF1, last = ${lastButtonPosition.toFixed(3)} in above CF7, count = ${resolvedButtonCount}, spacing = ${(buttonSpacing || 0).toFixed(3)} in.`,
    'Shirt front length base = Shirt back length - Bk to FT HPS; drawn front length is vertical FHPS→CF7.',
    'Seat level is constrained: CF6 stays aligned to CB6. Tail depths are separate: Back controls CB7→CB6 and Front controls CF6→CF7.',
    'Default hem start tangents at BSP and FSP are mirrored-horizontal to avoid edge kinks when BSP/FSP are matched.',
    'Hem modes: 0 = classic, 1 = S-curve profile. Both enforce side seam smoothness and 90° centerline tangency at CB7/CF7.',
    'Adaptive side seam engine: ΔCW=(Waist−Chest), ΔWB=(Belly−Waist), ΔBS=(Seat−Belly). CP_A shapes chest→waist suppression, CP_B auto-balances waist→belly continuity, and CP_C controls seat entry with proportional tension scaling.',
    'Shoulder strap constraint: FLPS X solved to keep |FHPS→FLPS| equal to |BHPS→BLPS|.',
    `Yoke depth cut: CB1→CBY = ${yokeDepth.toFixed(3)} in (measured down from CB1).`,
    `Front yoke rollover cuts: neck from FHPS = ${frontNeckRollover.toFixed(3)} in, armhole from FLPS = ${frontArmholeRollover.toFixed(3)} in, then transformed to yoke by mapping FHPS→BHPS and clockwise rotation until FLPS aligns BLPS.`,
    `Back armhole yoke dart split: midpoint is fixed at 50% of CBY→BYA; yoke BYA shifts toward BLPS by half dart width and back-panel BYA shifts toward BXB by half dart width.`,
    hasBackYokeDart
      ? `Total armhole circumference subtracts yoke dart intake (${yokeDartWidth.toFixed(3)} in per half pattern) from back armhole length.`
      : 'Total armhole circumference uses full drafted front+back armhole lengths (no yoke dart subtraction).',
    'Dart curve offset sliders are normalized around a production preset baseline; for new patterns, 0/0 represents the tuned default dart shape.',
    hasBackYokeDart
      ? 'Each dart half is represented as a simple quadratic Bézier from midpoint to shifted BYA (with editable control handles).'
      : 'When yoke dart width is 0, BYDY/BYDB dart split points and dart Bézier handles are disabled.',
    isShortSleeve
      ? 'Sleeve draft constants (short sleeve): no cuff deduction, no placket feature, no elbow control input.'
      : `Sleeve draft constants: Cuff Height = ${sleeveCuffHeight.toFixed(3)} in, Pleat Allowance = ${sleevePleatAllowance.toFixed(3)} in, Placket Height = ${sleevePlacketHeight.toFixed(3)} in.`,
    `Sleeve width input mode: ${legacySleeveClosedInputMode ? 'legacy closed-width detected and converted to unwrapped' : 'unwrapped width mode'} (Bicep=${bicepWidth.toFixed(3)} in, Elbow=${elbowWidth.toFixed(3)} in, Cuff=${maxCuffWidth.toFixed(3)} in).`,
    `Sleeve cap solver target: (Front Armhole + Back Armhole) + 0.125 = ${sleeveTargetCapPath.toFixed(3)} in; solved cap path = ${bestSleeve.capPathLength.toFixed(3)} in with cap height ${bestSleeve.capHeight.toFixed(3)} in.`,
    `Pitch points are percentage-based: FP = ${(sleeveFrontPitchPercent * 100).toFixed(2)}% of cap height, BP = ${(sleeveBackPitchPercent * 100).toFixed(2)}% of cap height.`,
    `Sleeve side seam validation: |BiC1→ELC1→HC1| = ${bestSleeve.sleeveFrontSeamLength.toFixed(3)} in, |BiC2→ELC2→HC2| = ${bestSleeve.sleeveBackSeamLength.toFixed(3)} in, Δ = ${sleeveSeamDelta.toFixed(4)} in.`,
    'Sleeve solver rounding rule: generated sleeve coordinates snap to nearest 0.0625 in (1/16).',
    'Dynamic notch sync active: sleeve SL1 notch and body underarm notch remain path-synchronized with sleeve cap edits.',
    `Fish Dart: ${fishDartEnabled ? 'ON' : 'OFF'}; Back Style: ${backStyle}.`,
    backStyle === 'side_pleat'
      ? (canApplySidePleat
        ? `Side Pleat applied on CBY→BYM→BYDB path; width=${backPleatWidth.toFixed(3)} in, length=${backPleatLength.toFixed(3)} in, position from BYDB=${backSidePleatOffsetFromBYDB.toFixed(3)} in.`
        : `Side Pleat inactive. Auto BYDB offset reference=${backSidePleatAutoOffsetFromBYDB.toFixed(3)} in when style=side_pleat.`)
      : backStyle === 'center_pleat'
      ? (canApplyCenterPleat
        ? `Center Pleat applied with side-pleat shift logic; CB seam is first fold, marker is on CBY→BYM at ${centerPleatMarkerDistanceFromCBY.toFixed(3)} in from CBY, length=${backPleatLength.toFixed(3)} in.`
        : 'Center Pleat inactive. Requires Yoke enabled with active Yoke Dart split points (BYM/BYDB).')
      : 'Back pleat style: none.',
  ];

  const totalWaistWidth = totalWaistHalf;
  const totalBellyWidth = totalBellyHalf;

  return {
    frontPath,
    backPath,
    dartPath,
    yokeDartPath,
    yokePath,
    sleevePath,
    sleeveLengthPath,
    sleevePlacketPath,
    sleeveWrappedMirrorPath,
    sleeveConstructionLines,
    notchMarks,
    frontBasePath,
    backBasePath,
    buttonExtensionPath,
    placketFoldPath,
    buttons: buttonCenters,
    buttonholes,
    fCreasePath: '',
    bCreasePath: '',
    frontMeasurements,
    backMeasurements,
    constructionLines,
    backPleatLines,
    pleatLines: [],
    viewBox,
    draftingStatus: 'ready',
    garmentType: 'shirt',
    formulaVersion: resolvedFormulaVersion,
    freeModeActive,
    pointDeltas: freePointDeltas,
    curveHandles: [
      {
        id: 'back-neck-ctrl',
        curveId: 'back-neck-curve',
        label: 'Back Neck Control',
        xKey: 'backNeckCtrlDx',
        yKey: 'backNeckCtrlDy',
        point: backCtrl,
        segmentStart: CB1,
        segmentEnd: BHPS,
        color: '#dc2626',
      },
      {
        id: 'front-neck-ctrl',
        curveId: 'front-neck-curve',
        label: 'Front Neck Control',
        xKey: 'frontNeckCtrlDx',
        yKey: 'frontNeckCtrlDy',
        point: frontCtrl,
        segmentStart: CF1,
        segmentEnd: FHPS,
        color: '#1e3a8a',
      },
      ...(!hasBackYokeDart ? [{
        id: 'back-armhole-part1-ctrl',
        curveId: 'back-armhole-part1-curve',
        label: 'Back Armhole Part 1 Control',
        xKey: 'backArmPart1CtrlDx',
        yKey: 'backArmPart1CtrlDy',
        point: backArmCtrl1,
        segmentStart: BLPS,
        segmentEnd: BXBResolved,
        color: '#dc2626',
      }] : []),
      {
        id: 'back-armhole-part2-ctrl',
        curveId: 'back-armhole-part2-curve',
        label: 'Back Armhole Part 2 Control',
        xKey: 'backArmPart2CtrlDx',
        yKey: 'backArmPart2CtrlDy',
        point: backArmCtrl2Resolved,
        segmentStart: BXBResolved,
        segmentEnd: BCPResolved,
        color: '#dc2626',
      },
      {
        id: 'front-armhole-part1-ctrl',
        curveId: 'front-armhole-part1-curve',
        label: 'Front Armhole Part 1 Control',
        xKey: 'frontArmPart1CtrlDx',
        yKey: 'frontArmPart1CtrlDy',
        point: frontArmCtrl1,
        segmentStart: FLPS,
        segmentEnd: FXF,
        color: '#1e3a8a',
      },
      {
        id: 'front-armhole-part2-ctrl',
        curveId: 'front-armhole-part2-curve',
        label: 'Front Armhole Part 2 Control',
        xKey: 'frontArmPart2CtrlDx',
        yKey: 'frontArmPart2CtrlDy',
        point: frontArmCtrl2,
        segmentStart: FXF,
        segmentEnd: FCP,
        color: '#1e3a8a',
      },
      {
        id: 'back-chest-waist-ctrl',
        curveId: 'back-side-chest-waist-curve',
        label: 'Back Chest→Waist Control',
        xKey: 'backChestWaistCtrlDx',
        yKey: 'backChestWaistCtrlDy',
        point: backChestWaistCtrlResolved,
        segmentStart: BCPResolved,
        segmentEnd: BWP,
        color: '#dc2626',
      },
      {
        id: 'back-waist-belly-ctrl',
        curveId: 'back-side-waist-belly-curve',
        label: 'Back Waist→Belly Control',
        xKey: 'backWaistBellyCtrlDx',
        yKey: 'backWaistBellyCtrlDy',
        point: backWaistBellyCtrl,
        segmentStart: BWP,
        segmentEnd: BBP,
        color: '#dc2626',
      },
      {
        id: 'back-belly-seat-ctrl',
        curveId: 'back-side-belly-seat-curve',
        label: 'Back Belly→Seat Control',
        xKey: 'backBellySeatCtrlDx',
        yKey: 'backBellySeatCtrlDy',
        point: backBellySeatCtrl,
        segmentStart: BBP,
        segmentEnd: BSP,
        color: '#dc2626',
      },
      {
        id: 'front-chest-waist-ctrl',
        curveId: 'front-side-chest-waist-curve',
        label: 'Front Chest→Waist Control',
        xKey: 'frontChestWaistCtrlDx',
        yKey: 'frontChestWaistCtrlDy',
        point: frontChestWaistCtrl,
        segmentStart: FCP,
        segmentEnd: FWP,
        color: '#1e3a8a',
      },
      {
        id: 'front-waist-belly-ctrl',
        curveId: 'front-side-waist-belly-curve',
        label: 'Front Waist→Belly Control',
        xKey: 'frontWaistBellyCtrlDx',
        yKey: 'frontWaistBellyCtrlDy',
        point: frontWaistBellyCtrl,
        segmentStart: FWP,
        segmentEnd: FBP,
        color: '#1e3a8a',
      },
      {
        id: 'front-belly-seat-ctrl',
        curveId: 'front-side-belly-seat-curve',
        label: 'Front Belly→Seat Control',
        xKey: 'frontBellySeatCtrlDx',
        yKey: 'frontBellySeatCtrlDy',
        point: frontBellySeatCtrl,
        segmentStart: FBP,
        segmentEnd: FSP,
        color: '#1e3a8a',
      },
      {
        id: 'back-hem-ctrl-1',
        curveId: 'back-hem-curve',
        label: 'Back Hem Control 1',
        xKey: 'backHemCtrl1Dx',
        yKey: 'backHemCtrl1Dy',
        point: backHemCtrl1,
        segmentStart: BSP,
        segmentEnd: BHP,
        color: '#dc2626',
      },
      {
        id: 'back-hem-ctrl-2',
        curveId: 'back-hem-curve',
        label: 'Back Hem Control 2',
        xKey: 'backHemCtrl2Dx',
        yKey: 'backHemCtrl2Dy',
        point: backHemCtrl2,
        segmentStart: BSP,
        segmentEnd: BHP,
        color: '#dc2626',
      },
      {
        id: 'front-hem-ctrl-1',
        curveId: 'front-hem-curve',
        label: 'Front Hem Control 1',
        xKey: 'frontHemCtrl1Dx',
        yKey: 'frontHemCtrl1Dy',
        point: frontHemCtrl1,
        segmentStart: FSP,
        segmentEnd: FHP,
        color: '#1e3a8a',
      },
      {
        id: 'front-hem-ctrl-2',
        curveId: 'front-hem-curve',
        label: 'Front Hem Control 2',
        xKey: 'frontHemCtrl2Dx',
        yKey: 'frontHemCtrl2Dy',
        point: frontHemCtrl2,
        segmentStart: FSP,
        segmentEnd: FHP,
        color: '#1e3a8a',
      },
      {
        id: 'sleeve-front-underarm-ctrl',
        curveId: 'sleeve-cap-front-underarm-curve',
        label: 'Sleeve Front Underarm',
        xKey: 'sleeveFrontUnderarmCtrlDx',
        yKey: 'sleeveFrontUnderarmCtrlDy',
        point: bestSleeve.controls.sleeveFrontUnderarmControl,
        segmentStart: bestSleeve.points.BiC1,
        segmentEnd: bestSleeve.points.FP,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-front-crown-ctrl',
        curveId: 'sleeve-cap-front-crown-curve',
        label: 'Sleeve Front Crown',
        xKey: 'sleeveFrontCrownCtrlDx',
        yKey: 'sleeveFrontCrownCtrlDy',
        point: bestSleeve.controls.sleeveFrontCrownControl,
        segmentStart: bestSleeve.points.FP,
        segmentEnd: bestSleeve.points.SL1,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-back-crown-ctrl',
        curveId: 'sleeve-cap-back-crown-curve',
        label: 'Sleeve Back Crown',
        xKey: 'sleeveBackCrownCtrlDx',
        yKey: 'sleeveBackCrownCtrlDy',
        point: bestSleeve.controls.sleeveBackCrownControl,
        segmentStart: bestSleeve.points.SL1,
        segmentEnd: bestSleeve.points.BP,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-back-underarm-ctrl',
        curveId: 'sleeve-cap-back-underarm-curve',
        label: 'Sleeve Back Underarm',
        xKey: 'sleeveBackUnderarmCtrlDx',
        yKey: 'sleeveBackUnderarmCtrlDy',
        point: bestSleeve.controls.sleeveBackUnderarmControl,
        segmentStart: bestSleeve.points.BP,
        segmentEnd: bestSleeve.points.BiC2,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-front-side-upper-ctrl',
        curveId: isShortSleeve ? 'sleeve-side-front-short-curve' : 'sleeve-side-front-upper-curve',
        label: isShortSleeve ? 'Sleeve Front Side (BiC1→HC1)' : 'Sleeve Front Side (BiC1→ELC1)',
        xKey: 'sleeveFrontSideUpperCtrlDx',
        yKey: 'sleeveFrontSideUpperCtrlDy',
        point: bestSleeve.controls.sleeveFrontSideUpperControl,
        segmentStart: bestSleeve.points.BiC1,
        segmentEnd: isShortSleeve ? bestSleeve.points.HC1 : bestSleeve.points.ELC1,
        color: '#7c3aed',
      },
      ...(!isShortSleeve ? [{
        id: 'sleeve-front-side-lower-ctrl',
        curveId: 'sleeve-side-front-lower-curve',
        label: 'Sleeve Front Side (ELC1→HC1)',
        xKey: 'sleeveFrontSideLowerCtrlDx',
        yKey: 'sleeveFrontSideLowerCtrlDy',
        point: bestSleeve.controls.sleeveFrontSideLowerControl,
        segmentStart: bestSleeve.points.ELC1,
        segmentEnd: bestSleeve.points.HC1,
        color: '#7c3aed',
      }] : []),
      {
        id: 'sleeve-back-side-upper-ctrl',
        curveId: isShortSleeve ? 'sleeve-side-back-short-curve' : 'sleeve-side-back-upper-curve',
        label: isShortSleeve ? 'Sleeve Back Side (BiC2→HC2)' : 'Sleeve Back Side (BiC2→ELC2)',
        xKey: 'sleeveBackSideUpperCtrlDx',
        yKey: 'sleeveBackSideUpperCtrlDy',
        point: bestSleeve.controls.sleeveBackSideUpperControl,
        segmentStart: bestSleeve.points.BiC2,
        segmentEnd: isShortSleeve ? bestSleeve.points.HC2 : bestSleeve.points.ELC2,
        color: '#7c3aed',
      },
      ...(!isShortSleeve ? [{
        id: 'sleeve-back-side-lower-ctrl',
        curveId: 'sleeve-side-back-lower-curve',
        label: 'Sleeve Back Side (ELC2→HC2)',
        xKey: 'sleeveBackSideLowerCtrlDx',
        yKey: 'sleeveBackSideLowerCtrlDy',
        point: bestSleeve.controls.sleeveBackSideLowerControl,
        segmentStart: bestSleeve.points.ELC2,
        segmentEnd: bestSleeve.points.HC2,
        color: '#7c3aed',
      }] : []),
      ...(hasBackYokeDart ? [
        {
          id: 'yoke-dart-ctrl',
          curveId: 'yoke-dart-curve',
          label: 'Yoke Dart Control',
          xKey: 'yokeDartYokeCtrlDx',
          yKey: 'yokeDartYokeCtrlDy',
          point: yokeDartControl,
          segmentStart: yokeDartMidpoint,
          segmentEnd: yokeDartBya,
          color: '#0e7490',
        },
        {
          id: 'back-yoke-dart-ctrl',
          curveId: 'back-yoke-dart-curve',
          label: 'Back Panel Yoke Dart Control',
          xKey: 'yokeDartBackCtrlDx',
          yKey: 'yokeDartBackCtrlDy',
          point: backPanelDartControlResolved,
          segmentStart: backPanelDartMidpointResolved,
          segmentEnd: backPanelDartByaResolved,
          color: '#0f766e',
        },
        {
          id: 'back-yoke-armhole-split-ctrl',
          curveId: 'back-yoke-armhole-split-curve',
          label: 'Back Yoke Armhole (BLPS→BYDY) Control',
          xKey: 'backYokeArmSplitCtrlDx',
          yKey: 'backYokeArmSplitCtrlDy',
          point: backYokeArmBezierControl,
          segmentStart: BLPS,
          segmentEnd: yokeDartBya,
          color: '#dc2626',
        },
        {
          id: 'back-panel-yoke-split-ctrl',
          curveId: 'back-panel-yoke-split-curve',
          label: 'Back Panel Armhole (BYDB→BXB) Control',
          xKey: 'backPanelYokeSplitCtrlDx',
          yKey: 'backPanelYokeSplitCtrlDy',
          point: backPanelYokeJoinControlResolved,
          segmentStart: backPanelDartByaResolved,
          segmentEnd: BXBResolved,
          color: '#dc2626',
        },
      ] : []),
    ].map((handle) => ({
      ...handle,
      point: toInternalPoint(handle.point),
      segmentStart: toInternalPoint(handle.segmentStart),
      segmentEnd: toInternalPoint(handle.segmentEnd),
    })),
    curveSegments: [
      {
        id: 'back-neck-curve',
        label: 'Back Neck',
        d: `M ${toSVG(CB1)} Q ${toSVG(backCtrl)} ${toSVG(BHPS)}`,
        color: '#dc2626',
      },
      {
        id: 'front-neck-curve',
        label: 'Front Neck',
        d: `M ${toSVG(CF1)} Q ${toSVG(frontCtrl)} ${toSVG(FHPS)}`,
        color: '#1e3a8a',
      },
      ...(!hasBackYokeDart ? [{
        id: 'back-armhole-part1-curve',
        label: 'Back Armhole Part 1',
        d: `M ${toSVG(BLPS)} Q ${toSVG(backArmCtrl1)} ${toSVG(BXBResolved)}`,
        color: '#dc2626',
      }] : [
        {
          id: 'back-yoke-armhole-split-curve',
          label: 'Back Yoke Armhole (BLPS→BYDY)',
          d: `M ${toSVG(BLPS)} Q ${toSVG(backYokeArmBezierControl)} ${toSVG(yokeDartBya)}`,
          color: '#dc2626',
        },
        {
          id: 'back-panel-yoke-split-curve',
          label: 'Back Panel Armhole (BYDB→BXB)',
          d: `M ${toSVG(backPanelDartByaResolved)} Q ${toSVG(backPanelYokeJoinControlResolved)} ${toSVG(BXBResolved)}`,
          color: '#dc2626',
        },
      ]),
      {
        id: 'back-armhole-part2-curve',
        label: 'Back Armhole Part 2',
        d: `M ${toSVG(BXBResolved)} Q ${toSVG(backArmCtrl2Resolved)} ${toSVG(BCPResolved)}`,
        color: '#dc2626',
      },
      {
        id: 'front-armhole-part1-curve',
        label: 'Front Armhole Part 1',
        d: `M ${toSVG(FLPS)} Q ${toSVG(frontArmCtrl1)} ${toSVG(FXF)}`,
        color: '#1e3a8a',
      },
      {
        id: 'front-armhole-part2-curve',
        label: 'Front Armhole Part 2',
        d: `M ${toSVG(FXF)} Q ${toSVG(frontArmCtrl2)} ${toSVG(FCP)}`,
        color: '#1e3a8a',
      },
      {
        id: 'back-side-chest-waist-curve',
        label: 'Back Chest→Waist Segment',
        d: `M ${toSVG(BCPResolved)} Q ${toSVG(backChestWaistCtrlResolved)} ${toSVG(BWP)}`,
        color: '#dc2626',
      },
      {
        id: 'back-side-waist-belly-curve',
        label: 'Back Waist→Belly Segment',
        d: `M ${toSVG(BWP)} Q ${toSVG(backWaistBellyCtrl)} ${toSVG(BBP)}`,
        color: '#dc2626',
      },
      {
        id: 'back-side-belly-seat-curve',
        label: 'Back Belly→Seat Segment',
        d: `M ${toSVG(BBP)} Q ${toSVG(backBellySeatCtrl)} ${toSVG(BSP)}`,
        color: '#dc2626',
      },
      {
        id: 'front-side-chest-waist-curve',
        label: 'Front Chest→Waist Segment',
        d: `M ${toSVG(FCP)} Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FWP)}`,
        color: '#1e3a8a',
      },
      {
        id: 'front-side-waist-belly-curve',
        label: 'Front Waist→Belly Segment',
        d: `M ${toSVG(FWP)} Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(FBP)}`,
        color: '#1e3a8a',
      },
      {
        id: 'front-side-belly-seat-curve',
        label: 'Front Belly→Seat Segment',
        d: `M ${toSVG(FBP)} Q ${toSVG(frontBellySeatCtrl)} ${toSVG(FSP)}`,
        color: '#1e3a8a',
      },
      {
        id: 'back-hem-curve',
        label: 'Back Hem Curve',
        d: `M ${toSVG(BSP)} C ${toSVG(backHemCtrl1)} ${toSVG(backHemCtrl2)} ${toSVG(BHP)}`,
        color: '#dc2626',
      },
      {
        id: 'front-hem-curve',
        label: 'Front Hem Curve',
        d: `M ${toSVG(FSP)} C ${toSVG(frontHemCtrl1)} ${toSVG(frontHemCtrl2)} ${toSVG(FHP)}`,
        color: '#1e3a8a',
      },
      {
        id: 'sleeve-cap-front-underarm-curve',
        label: 'Sleeve Cap Front Underarm',
        d: `M ${toSVG(bestSleeve.points.BiC1)} Q ${toSVG(bestSleeve.controls.sleeveFrontUnderarmControl)} ${toSVG(bestSleeve.points.FP)}`,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-cap-front-crown-curve',
        label: 'Sleeve Cap Front Crown',
        d: `M ${toSVG(bestSleeve.points.FP)} Q ${toSVG(bestSleeve.controls.sleeveFrontCrownControl)} ${toSVG(bestSleeve.points.SL1)}`,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-cap-back-crown-curve',
        label: 'Sleeve Cap Back Crown',
        d: `M ${toSVG(bestSleeve.points.SL1)} Q ${toSVG(bestSleeve.controls.sleeveBackCrownControl)} ${toSVG(bestSleeve.points.BP)}`,
        color: '#7c3aed',
      },
      {
        id: 'sleeve-cap-back-underarm-curve',
        label: 'Sleeve Cap Back Underarm',
        d: `M ${toSVG(bestSleeve.points.BP)} Q ${toSVG(bestSleeve.controls.sleeveBackUnderarmControl)} ${toSVG(bestSleeve.points.BiC2)}`,
        color: '#7c3aed',
      },
      {
        id: isShortSleeve ? 'sleeve-side-front-short-curve' : 'sleeve-side-front-upper-curve',
        label: isShortSleeve ? 'Sleeve Side Front' : 'Sleeve Side Front Upper',
        d: isShortSleeve
          ? `M ${toSVG(bestSleeve.points.BiC1)} Q ${toSVG(bestSleeve.controls.sleeveFrontSideUpperControl)} ${toSVG(bestSleeve.points.HC1)}`
          : `M ${toSVG(bestSleeve.points.BiC1)} Q ${toSVG(bestSleeve.controls.sleeveFrontSideUpperControl)} ${toSVG(bestSleeve.points.ELC1)}`,
        color: '#7c3aed',
      },
      ...(!isShortSleeve ? [{
        id: 'sleeve-side-front-lower-curve',
        label: 'Sleeve Side Front Lower',
        d: `M ${toSVG(bestSleeve.points.ELC1)} Q ${toSVG(bestSleeve.controls.sleeveFrontSideLowerControl)} ${toSVG(bestSleeve.points.HC1)}`,
        color: '#7c3aed',
      }] : []),
      {
        id: isShortSleeve ? 'sleeve-side-back-short-curve' : 'sleeve-side-back-upper-curve',
        label: isShortSleeve ? 'Sleeve Side Back' : 'Sleeve Side Back Upper',
        d: isShortSleeve
          ? `M ${toSVG(bestSleeve.points.BiC2)} Q ${toSVG(bestSleeve.controls.sleeveBackSideUpperControl)} ${toSVG(bestSleeve.points.HC2)}`
          : `M ${toSVG(bestSleeve.points.BiC2)} Q ${toSVG(bestSleeve.controls.sleeveBackSideUpperControl)} ${toSVG(bestSleeve.points.ELC2)}`,
        color: '#7c3aed',
      },
      ...(!isShortSleeve ? [{
        id: 'sleeve-side-back-lower-curve',
        label: 'Sleeve Side Back Lower',
        d: `M ${toSVG(bestSleeve.points.ELC2)} Q ${toSVG(bestSleeve.controls.sleeveBackSideLowerControl)} ${toSVG(bestSleeve.points.HC2)}`,
        color: '#7c3aed',
      }] : []),
      ...(hasBackYokeDart ? [
        {
          id: 'yoke-dart-curve',
          label: 'Yoke Dart Curve',
          d: `M ${toSVG(yokeDartMidpoint)} Q ${toSVG(yokeDartControl)} ${toSVG(yokeDartBya)}`,
          color: '#0e7490',
        },
        {
          id: 'back-yoke-dart-curve',
          label: 'Back Panel Yoke Dart Curve',
          d: `M ${toSVG(backPanelDartMidpointResolved)} Q ${toSVG(backPanelDartControlResolved)} ${toSVG(backPanelDartByaResolved)}`,
          color: '#0f766e',
        },
      ] : []),
    ],
    namedPoints: [
      ...toNamedPoints('front', frontPoints),
      ...toNamedPoints('back', backPoints),
      ...toNamedPoints('sleeve', sleevePoints),
    ],
    pointReferences,
    formulaNarrative: {
      phase: 'base_block_only',
      scope: yokeEnabled
        ? ['front_panel', 'back_panel', 'yoke', 'sleeve', 'fish_dart', 'neck_curves', 'armhole_curves']
        : ['front_panel', 'back_panel', 'sleeve', 'fish_dart', 'neck_curves', 'armhole_curves'],
      deferred: (() => {
        const baseDeferred = yokeEnabled ? ['collar'] : ['yoke_separation', 'collar'];
        if (!isShortSleeve) baseDeferred.push('cuff');
        return baseDeferred;
      })(),
    },
    matchingSummary: {
      shoulderBackLength: Number(backShoulderLength.toFixed(3)),
      shoulderFrontLength: Number(dist(FHPS, FLPS).toFixed(3)),
      backShoulderAngleDegrees: Number(backShoulderAngleDegrees.toFixed(3)),
      frontShoulderAngleDegrees: Number(frontShoulderAngleDegrees.toFixed(3)),
      autoFrontNeckWidth: Number(autoFrontNeckWidth.toFixed(3)),
      autoBackNeckWidth: Number(autoBackNeckWidth.toFixed(3)),
      autoFrontNeckDepth: Number(autoFrontNeckDepth.toFixed(3)),
      autoBackNeckDepth: Number(autoBackNeckDepth.toFixed(3)),
      resolvedFrontNeckWidth: Number(resolvedFrontNeckWidth.toFixed(3)),
      resolvedBackNeckWidth: Number(resolvedBackNeckWidth.toFixed(3)),
      resolvedFrontNeckDepth: Number(resolvedFrontNeckDepth.toFixed(3)),
      resolvedBackNeckDepth: Number(resolvedBackNeckDepth.toFixed(3)),
      autoAcrossBackDepth: Number(autoAcrossBackDepth.toFixed(3)),
      resolvedAcrossBackDepth: Number(resolvedAcrossBackDepth.toFixed(3)),
      autoAcrossFrontDepth: Number(autoAcrossFrontDepth.toFixed(3)),
      resolvedAcrossFrontDepth: Number(resolvedAcrossFrontDepth.toFixed(3)),
      neckBackCurve: Number(backNeckLenAdjusted.toFixed(3)),
      neckFrontCurve: Number(frontNeckLenAdjusted.toFixed(3)),
      neckHalfActual: Number(halfNeckLengthAdjusted.toFixed(4)),
      neckHalfTargetMin: Number(targetHalfNeckMin.toFixed(4)),
      neckHalfTargetMax: Number(targetHalfNeckMax.toFixed(4)),
      totalNeckHalf: Number(totalNeckHalf.toFixed(3)),
      totalNeckFull: Number((totalNeckHalf * 2).toFixed(3)),
      totalArmholeHalf: Number(totalArmholeHalf.toFixed(3)),
      totalArmholeFull: Number((totalArmholeHalf * 2).toFixed(3)),
      armholeFrontHalf: Number(frontArmholeHalf.toFixed(3)),
      armholeBackHalf: Number(backArmholeHalf.toFixed(3)),
      armholeBackHalfRaw: Number(backArmholeHalfRaw.toFixed(3)),
      armholeDartReductionHalf: Number(armholeDartReductionHalf.toFixed(3)),
      scyeDepthFront: Number(frontScyeDepth.toFixed(3)),
      scyeDepthBack: Number(backScyeDepth.toFixed(3)),
      scyeDepthAverage: Number(avgScyeDepth.toFixed(3)),
      shirtLengthBackInput: Number(shirtLengthBack.toFixed(3)),
      shirtLengthBackDrawn: Number(shirtLengthBackDrawn.toFixed(3)),
      shirtLengthFrontAuto: Number(shirtLengthFrontAuto.toFixed(3)),
      shirtLengthFrontDrawn: Number(shirtLengthFrontDrawn.toFixed(3)),
      tailDepthBack: Number(tailDepthBack.toFixed(3)),
      tailDepthFront: Number(tailDepthFront.toFixed(3)),
      frontChestHalf: Number(frontChestWidthResolved.toFixed(3)),
      backChestHalf: Number(backChestWidthResolved.toFixed(3)),
      totalChestHalf: Number(totalChestHalf.toFixed(3)),
      totalChestFull: Number((totalChestHalf * 2).toFixed(3)),
      shoulderHorizontalHalf: Number(shoulderHorizontalLine.toFixed(3)),
      shoulderHorizontalFull: Number((shoulderHorizontalLine * 2).toFixed(3)),
      acrossBackHalf: Number(acrossBackLine.toFixed(3)),
      acrossBackFull: Number((acrossBackLine * 2).toFixed(3)),
      acrossFrontHalf: Number(acrossFrontLine.toFixed(3)),
      acrossFrontFull: Number((acrossFrontLine * 2).toFixed(3)),
      frontWaistLine: Number(frontWaistLine.toFixed(3)),
      backWaistRawLine: Number(backWaistRawLine.toFixed(3)),
      waistDartIntake: Number(waistDartLine.toFixed(3)),
      backWaistNetLine: Number(backWaistNetLine.toFixed(3)),
      totalWaistRawHalf: Number(totalWaistRawHalf.toFixed(3)),
      totalWaistRawFull: Number((totalWaistRawHalf * 2).toFixed(3)),
      totalWaistWidth: Number(totalWaistWidth.toFixed(3)),
      totalWaistFull: Number((totalWaistWidth * 2).toFixed(3)),
      targetWaistFull: Number(waist.toFixed(3)),
      frontBellyLine: Number(frontBellyLine.toFixed(3)),
      backBellyRawLine: Number(backBellyRawLine.toFixed(3)),
      bellyDartIntake: Number(bellyDartLine.toFixed(3)),
      backBellyNetLine: Number(backBellyNetLine.toFixed(3)),
      totalBellyRawHalf: Number(totalBellyRawHalf.toFixed(3)),
      totalBellyRawFull: Number((totalBellyRawHalf * 2).toFixed(3)),
      totalBellyWidth: Number(totalBellyWidth.toFixed(3)),
      totalBellyFull: Number((totalBellyWidth * 2).toFixed(3)),
      targetBellyFull: Number(belly.toFixed(3)),
      totalSeatHalf: Number(totalSeatHalf.toFixed(3)),
      totalSeatFull: Number((totalSeatHalf * 2).toFixed(3)),
      targetSeatFull: Number(seat.toFixed(3)),
      frontSeatHalf: Number(frontSeatWidthResolved.toFixed(3)),
      backSeatHalf: Number(backSeatWidthResolved.toFixed(3)),
      portlyType,
      frontWaistHalfRaw: Number(frontWaistWidthResolved.toFixed(3)),
      backWaistHalfRaw: Number(backWaistWidthResolved.toFixed(3)),
      frontBellyHalfRaw: Number(frontBellyWidthResolved.toFixed(3)),
      backBellyHalfRaw: Number(backBellyWidthResolved.toFixed(3)),
      frontSideChestToWaist: Number(frontSideChestToWaist.toFixed(4)),
      backSideChestToWaist: Number(backSideChestToWaist.toFixed(4)),
      sideChestToWaistDelta: Number((frontSideChestToWaist - backSideChestToWaist).toFixed(4)),
      frontSideWaistToBelly: Number(frontSideWaistToBelly.toFixed(4)),
      backSideWaistToBelly: Number(backSideWaistToBelly.toFixed(4)),
      sideWaistToBellyDelta: Number((frontSideWaistToBelly - backSideWaistToBelly).toFixed(4)),
      frontSideBellyToSeat: Number(frontSideBellyToSeat.toFixed(4)),
      backSideBellyToSeat: Number(backSideBellyToSeat.toFixed(4)),
      sideBellyToSeatDelta: Number((frontSideBellyToSeat - backSideBellyToSeat).toFixed(4)),
      waistAdjustmentSteps: Number(waistAdjusted.steps),
      bellyAdjustmentSteps: Number(bellyAdjusted.steps),
      yokeDepth: Number(yokeDepth.toFixed(3)),
      yokeCutY: Number(CBY.y.toFixed(3)),
      yokeEnabled,
      cuffWidth: Number(sleeveCuffHeight.toFixed(3)),
      sleeveDisplayedLength: Number(sleeveDisplayedLength.toFixed(3)),
      frontNeckRollover: Number(frontNeckRollover.toFixed(3)),
      frontArmholeRollover: Number(frontArmholeRollover.toFixed(3)),
      yokeDartWidth: Number(yokeDartWidth.toFixed(3)),
      yokeDartLength: Number(yokeDartLength.toFixed(3)),
      hasBackYokeDart,
      deltaCW: Number((waist - chest).toFixed(3)),
      deltaWB: Number((belly - waist).toFixed(3)),
      deltaBS: Number((seat - belly).toFixed(3)),
      buttonExtensionWidth: Number(buttonExtensionWidth.toFixed(3)),
      placketFoldWidth: Number(placketFoldWidth.toFixed(3)),
      buttonCount: Number(resolvedButtonCount),
      buttonCountMode: manualButtonCount >= 2 ? 'manual' : 'auto',
      firstButtonPosition: Number(firstButtonPosition.toFixed(3)),
      lastButtonPosition: Number(lastButtonPosition.toFixed(3)),
      buttonSpacing: Number((buttonSpacing || 0).toFixed(3)),
      sleeveCapHeight: Number(bestSleeve.capHeight.toFixed(4)),
      sleeveCapTarget: Number(sleeveTargetCapPath.toFixed(4)),
      sleeveCapPathLength: Number(bestSleeve.capPathLength.toFixed(4)),
      sleeveDraftLength: Number(sleeveDraftLength.toFixed(4)),
      sleeveDistToElbowAuto: Number((isShortSleeve
        ? (bestSleeve.capHeight + ((sleeveDraftLength - bestSleeve.capHeight) * 0.55))
        : (bestSleeve.capHeight + ((sleeveDraftLength - bestSleeve.capHeight) * 0.5) - 1)).toFixed(4)),
      sleeveDistToElbow: Number(bestSleeve.distToElbow.toFixed(4)),
      sleeveStyle,
      sleeveHasPlacket: !isShortSleeve,
      sleeveCuffDeductionApplied: !isShortSleeve,
      sleeveFrontPitchPercent: Number((sleeveFrontPitchPercent * 100).toFixed(3)),
      sleeveBackPitchPercent: Number((sleeveBackPitchPercent * 100).toFixed(3)),
      sleeveFrontSeamLength: Number(bestSleeve.sleeveFrontSeamLength.toFixed(4)),
      sleeveBackSeamLength: Number(bestSleeve.sleeveBackSeamLength.toFixed(4)),
      sleeveSideSeamDelta: Number(sleeveSeamDelta.toFixed(5)),
      sleeveWidthInputMode: legacySleeveClosedInputMode ? 'legacy_closed_auto_converted' : 'unwrapped',
      sleeveBicepUnwrappedInput: Number(bicepWidth.toFixed(4)),
      sleeveElbowUnwrappedInput: Number(elbowWidth.toFixed(4)),
      sleeveHemUnwrappedInput: Number(maxCuffWidth.toFixed(4)),
      sleeveBicepUnwrappedDrawn: Number(sleeveBicepUnwrappedDrawn.toFixed(4)),
      sleeveElbowUnwrappedDrawn: Number(sleeveElbowUnwrappedDrawn.toFixed(4)),
      sleeveHemUnwrappedDrawn: Number(sleeveHemUnwrappedDrawn.toFixed(4)),
      sleeveNotchSL1ToFP: Number(bestSleeve.capFrontCrownLength.toFixed(4)),
      sleeveNotchSL1ToBP: Number(bestSleeve.capBackCrownLength.toFixed(4)),
      sleeveNotchSL1ToBiC1: Number(sleeveToFrontUnderarmDistance.toFixed(4)),
      bodyArmholeFrontPath: Number(frontArmholeLength.toFixed(4)),
      bodyArmholeBackPath: Number(backArmholeLength.toFixed(4)),
      bodyUnderarmNotchPiece: bodyUnderarmPiece,
      notchCount: notchMarks.reduce((sum, mark) => sum + (mark.paths?.length || 0), 0),
      fishDartEnabled,
      backStyle,
      backPleatWidth: Number(backPleatWidth.toFixed(4)),
      backPleatLength: Number(backPleatLength.toFixed(4)),
      backSidePleatAutoOffsetFromBYDB: Number(backSidePleatAutoOffsetFromBYDB.toFixed(4)),
      backSidePleatOffsetFromBYDB: Number(backSidePleatOffsetFromBYDB.toFixed(4)),
      backSidePleatResolvedFromBYDB: Number(backSidePleatOffsetFromBYDB.toFixed(4)),
      backCenterPleatMarkerFromCBY: Number(centerPleatMarkerDistanceFromCBY.toFixed(4)),
      backPleatApplied: canApplyBackPleatShift,
      backSidePleatApplied: canApplySidePleat,
      backCenterPleatApplied: canApplyCenterPleat,
    },
  };
}

export function generateShirtPatternSIyV1(measurements, controls = {}) {
  return generateShirtPatternBase(measurements, controls, {
    formulaVersion: SHIRT_V1_FORMULA_VERSION,
    defaultLastButtonPosition: 3.5,
    preserveArmPart2CtrlYOnXDrag: false,
    decoupleArmholeSplitControls: false,
    applyNetCompensatedDartSplit: false,
  });
}

export function generateShirtPatternSIyV2(measurements, controls = {}) {
  return generateShirtPatternBase(measurements, controls, {
    formulaVersion: SHIRT_V2_FORMULA_VERSION,
    defaultLastButtonPosition: 5.5,
    preserveArmPart2CtrlYOnXDrag: true,
    decoupleArmholeSplitControls: true,
    applyNetCompensatedDartSplit: true,
  });
}

const toDraftFromInternal = (point) => (
  point && Number.isFinite(Number(point.x)) && Number.isFinite(Number(point.y))
    ? { x: Number(point.x), y: -Number(point.y) }
    : null
);

const collectShirtCollarBandArgs = (measurements, body, namedDraft, frontCtrl) => ({
  CF1: namedDraft.CF1,
  FHPS: namedDraft.FHPS,
  frontCtrl,
  E1: namedDraft.E1,
  halfNeckLength: body.matchingSummary?.totalNeckHalf || body.matchingSummary?.neckHalfActual,
  frontNeckLength: body.matchingSummary?.neckFrontCurve,
  buttonExtensionWidth: n(measurements.buttonExtensionWidth, 0.625),
  frontNeckTracePercent: n(measurements.collarBandFrontNeckPercent, 30),
  bandBackHeight: n(measurements.collarBandBackHeight, 1.25),
  bandFrontHeight: n(measurements.collarBandFrontHeight, 1),
  layoutOrigin: {
    x: 1,
    y: Math.min(namedDraft.CF1?.y || 0, namedDraft.FHPS?.y || 0) - n(measurements.collarBandBackHeight, 1.25) - 2.75,
  },
});

export function recomputeShirtCollarBandV3(measurements, controls = {}) {
  const body = generateShirtPatternSIyV2(measurements, {
    ...controls,
    formulaVersion: SHIRT_V3_FORMULA_VERSION,
  });
  const namedDraft = Object.fromEntries(
    (body.namedPoints || [])
      .filter((point) => point?.name)
      .map((point) => [point.name, toDraftFromInternal(point)]),
  );
  const frontNeckHandle = (body.curveHandles || []).find((handle) => handle?.id === 'front-neck-ctrl');
  const layoutB0Dy = -n(controls?.pointDeltas?.CbB0?.y, 0);
  const layoutB1Dy = -n(controls?.pointDeltas?.CbB1?.y, 0);
  return recomputeShirtCollarBandGeometry({
    ...collectShirtCollarBandArgs(
      measurements,
      body,
      namedDraft,
      toDraftFromInternal(frontNeckHandle?.point),
    ),
    layoutB0Dy,
    layoutB1Dy,
    bandBackManual: n(measurements.collarBandBackManual, 0),
  }, {
    preserveBackPoints: Math.abs(n(measurements.collarBandBackManual, 0)) > 1e-8
      || Math.abs(n(controls?.pointDeltas?.CbB0?.y, 0)) > 1e-8
      || Math.abs(n(controls?.pointDeltas?.CbB1?.y, 0)) > 1e-8
      || Math.abs(n(controls?.pointDeltas?.CbB0?.x, 0)) > 1e-8
      || Math.abs(n(controls?.pointDeltas?.CbB1?.x, 0)) > 1e-8,
  });
}

export function generateShirtPatternSIyV3(measurements, controls = {}) {
  const body = generateShirtPatternSIyV2(measurements, {
    ...controls,
    formulaVersion: SHIRT_V3_FORMULA_VERSION,
  });

  const namedDraft = Object.fromEntries(
    (body.namedPoints || [])
      .filter((point) => point?.name)
      .map((point) => [point.name, toDraftFromInternal(point)]),
  );
  const frontNeckHandle = (body.curveHandles || []).find((handle) => handle?.id === 'front-neck-ctrl');
  const CF1 = namedDraft.CF1;
  const FHPS = namedDraft.FHPS;
  const frontCtrl = toDraftFromInternal(frontNeckHandle?.point);
  const sharedArgs = collectShirtCollarBandArgs(measurements, body, namedDraft, frontCtrl);

  const collar = buildShirtCollarBand({
    ...sharedArgs,
    topStartCtrlDx: n(measurements.collarBandTopStartCtrlDx, 0),
    topStartCtrlDy: n(measurements.collarBandTopStartCtrlDy, 0),
    topEndCtrlDx: n(measurements.collarBandTopEndCtrlDx, 0),
    topEndCtrlDy: n(measurements.collarBandTopEndCtrlDy, 0),
    topFollowBottom: measurements.collarBandTopFollowBottom !== 0 && measurements.collarBandTopFollowBottom !== '0' && measurements.collarBandTopFollowBottom !== false,
    topStartAbsX: n(measurements.collarBandTopStartAbsX, 0),
    topStartAbsY: n(measurements.collarBandTopStartAbsY, 0),
    topEndAbsX: n(measurements.collarBandTopEndAbsX, 0),
    topEndAbsY: n(measurements.collarBandTopEndAbsY, 0),
    bottomStartCtrlDx: n(measurements.collarBandBottomStartCtrlDx, 0),
    bottomStartCtrlDy: n(measurements.collarBandBottomStartCtrlDy, 0),
    bottomEndCtrlDx: n(measurements.collarBandBottomEndCtrlDx, 0),
    bottomEndCtrlDy: n(measurements.collarBandBottomEndCtrlDy, 0),
    neckCtrlDx: n(measurements.collarBandNeckCtrlDx, 0),
    neckCtrlDy: n(measurements.collarBandNeckCtrlDy, 0),
    layoutB0Dy: -n(controls?.pointDeltas?.CbB0?.y, 0),
    layoutB1Dy: -n(controls?.pointDeltas?.CbB1?.y, 0),
    layoutOrigin: {
      x: 1,
      y: Math.min(CF1?.y || 0, FHPS?.y || 0) - n(measurements.collarBandBackHeight, 1.25) - 2.75,
    },
  });

  if (!collar) {
    return {
      ...body,
      formulaVersion: SHIRT_V3_FORMULA_VERSION,
      geometryVersion: controls.geometryVersion || 'shirt_geom_v3',
    };
  }

  const layoutSelectable = new Set(['CbB0', 'CbB1']);
  const collarNamedPoints = [
    ...Object.entries(collar.points).map(([name, point]) => ({
      name,
      piece: 'collar',
      x: point.x,
      y: -point.y,
      selectable: false,
      renderNode: name !== 'CbF1',
      showLabel: name !== 'CbF1',
    })),
    ...Object.entries(collar.layoutPoints || {}).map(([name, point]) => ({
      name,
      piece: 'collarLayout',
      x: point.x,
      y: -point.y,
      selectable: layoutSelectable.has(name),
      renderNode: name !== 'CbF1',
      showLabel: name !== 'CbF1',
    })),
  ];
  const flip = (point) => ({ x: point.x, y: -point.y });
  const layoutCos = collar.layoutTransform?.cos;
  const layoutSin = collar.layoutTransform?.sin;
  const collarCurveHandles = [
    ...(collar.bottomCurve ? [
      {
        id: 'collar-band-bottom-start-ctrl',
        curveId: 'collar-band-bottom-curve',
        label: 'Collar Band Neck Start (CbB0→CbF0)',
        xKey: 'collarBandBottomStartCtrlDx',
        yKey: 'collarBandBottomStartCtrlDy',
        point: flip(collar.bottomCurve.startCtrl),
        segmentStart: flip(collar.bottomCurve.start),
        segmentEnd: flip(collar.bottomCurve.end),
        color: '#0f766e',
      },
      {
        id: 'collar-band-bottom-end-ctrl',
        curveId: 'collar-band-bottom-curve',
        label: 'Collar Band Neck End (CbB0→CbF0)',
        xKey: 'collarBandBottomEndCtrlDx',
        yKey: 'collarBandBottomEndCtrlDy',
        point: flip(collar.bottomCurve.endCtrl),
        segmentStart: flip(collar.bottomCurve.start),
        segmentEnd: flip(collar.bottomCurve.end),
        color: '#0f766e',
      },
      {
        id: 'collar-band-bottom-start-ctrl-layout',
        curveId: 'collar-band-bottom-curve-layout',
        label: 'Collar Band Neck Start (CbB0→CbF0)',
        xKey: 'collarBandBottomStartCtrlDx',
        yKey: 'collarBandBottomStartCtrlDy',
        point: flip(collar.layoutBottomStartCtrl),
        segmentStart: flip(collar.layoutPoints.CbB0),
        segmentEnd: flip(collar.layoutPoints.CbF0),
        color: '#0f766e',
        layoutCos,
        layoutSin,
      },
      {
        id: 'collar-band-bottom-end-ctrl-layout',
        curveId: 'collar-band-bottom-curve-layout',
        label: 'Collar Band Neck End (CbB0→CbF0)',
        xKey: 'collarBandBottomEndCtrlDx',
        yKey: 'collarBandBottomEndCtrlDy',
        point: flip(collar.layoutBottomEndCtrl),
        segmentStart: flip(collar.layoutPoints.CbB0),
        segmentEnd: flip(collar.layoutPoints.CbF0),
        color: '#0f766e',
        layoutCos,
        layoutSin,
      },
    ] : []),
    ...(collar.topCurve ? [
      {
        id: 'collar-band-top-start-ctrl',
        curveId: 'collar-band-top-curve',
        label: 'Collar Band Top Start (CbB1→CbE1)',
        xKey: 'collarBandTopStartCtrlDx',
        yKey: 'collarBandTopStartCtrlDy',
        point: flip(collar.topCurve.startCtrl),
        segmentStart: flip(collar.topCurve.start),
        segmentEnd: flip(collar.topCurve.end),
        color: '#0f766e',
      },
      {
        id: 'collar-band-top-end-ctrl',
        curveId: 'collar-band-top-curve',
        label: 'Collar Band Top End (CbB1→CbE1)',
        xKey: 'collarBandTopEndCtrlDx',
        yKey: 'collarBandTopEndCtrlDy',
        point: flip(collar.topCurve.endCtrl),
        segmentStart: flip(collar.topCurve.start),
        segmentEnd: flip(collar.topCurve.end),
        color: '#0f766e',
      },
      {
        id: 'collar-band-top-start-ctrl-layout',
        curveId: 'collar-band-top-curve-layout',
        label: 'Collar Band Top Start (CbB1→CbE1)',
        xKey: 'collarBandTopStartCtrlDx',
        yKey: 'collarBandTopStartCtrlDy',
        point: flip(collar.layoutTopStartCtrl),
        segmentStart: flip(collar.layoutPoints.CbB1),
        segmentEnd: flip(collar.layoutPoints.CbE1),
        color: '#0f766e',
        layoutCos,
        layoutSin,
      },
      {
        id: 'collar-band-top-end-ctrl-layout',
        curveId: 'collar-band-top-curve-layout',
        label: 'Collar Band Top End (CbB1→CbE1)',
        xKey: 'collarBandTopEndCtrlDx',
        yKey: 'collarBandTopEndCtrlDy',
        point: flip(collar.layoutTopEndCtrl),
        segmentStart: flip(collar.layoutPoints.CbB1),
        segmentEnd: flip(collar.layoutPoints.CbE1),
        color: '#0f766e',
        layoutCos,
        layoutSin,
      },
    ] : []),
  ];
  const leaf = buildShirtCollarLeaf({
    layoutB0: collar.layoutPoints.CbB0,
    layoutB1: collar.layoutPoints.CbB1,
    layoutE1: collar.layoutPoints.CbE1,
    layoutTopStartCtrl: collar.layoutTopStartCtrl,
    layoutTopEndCtrl: collar.layoutTopEndCtrl,
    leafBackHeight: n(measurements.collarLeafBackHeight, 1.75),
    tieGap: n(measurements.collarLeafTieGap, 1),
    pointLength: n(measurements.collarLeafPointLength, 3),
    spread: n(measurements.collarLeafSpread, 0),
    topStartCtrlDx: n(measurements.collarLeafTopStartCtrlDx, 0),
    topStartCtrlDy: n(measurements.collarLeafTopStartCtrlDy, 0),
    topEndCtrlDx: n(measurements.collarLeafTopEndCtrlDx, 0),
    topEndCtrlDy: n(measurements.collarLeafTopEndCtrlDy, 0),
    backInsetDx: n(controls?.pointDeltas?.ClB0?.x, 0),
  });
  const onBodyLeaf = leaf ? buildShirtCollarLeafOnBody(leaf, collar.layoutTransform) : null;
  const leafNamedPoints = leaf
    ? [
      ...Object.entries(leaf.layoutPoints).map(([name, point]) => ({
        name,
        piece: 'collarLayout',
        x: point.x,
        y: -point.y,
        selectable: name === 'ClB0',
      })),
      ...(onBodyLeaf?.points
        ? Object.entries(onBodyLeaf.points).map(([name, point]) => ({
          name,
          piece: 'collar',
          x: point.x,
          y: -point.y,
          selectable: false,
        }))
        : []),
    ]
    : [];
  const leafCurveHandles = leaf?.topCurve ? [
    {
      id: 'collar-leaf-top-start-ctrl-layout',
      curveId: 'collar-leaf-top-curve-layout',
      label: 'Collar Leaf Top Start (ClB0→ClP1)',
      xKey: 'collarLeafTopStartCtrlDx',
      yKey: 'collarLeafTopStartCtrlDy',
      point: flip(leaf.topCurve.startCtrl),
      segmentStart: flip(leaf.topCurve.start),
      segmentEnd: flip(leaf.topCurve.end),
      color: '#0f766e',
    },
    {
      id: 'collar-leaf-top-end-ctrl-layout',
      curveId: 'collar-leaf-top-curve-layout',
      label: 'Collar Leaf Top End (ClB0→ClP1)',
      xKey: 'collarLeafTopEndCtrlDx',
      yKey: 'collarLeafTopEndCtrlDy',
      point: flip(leaf.topCurve.endCtrl),
      segmentStart: flip(leaf.topCurve.start),
      segmentEnd: flip(leaf.topCurve.end),
      color: '#0f766e',
    },
  ] : [];
  const leafCurveSegments = leaf?.topCurve ? [
    {
      id: 'collar-leaf-top-curve-layout',
      label: 'Collar Leaf Top (ClB0→ClP1)',
      d: `M ${leaf.topCurve.start.x} ${leaf.topCurve.start.y} C ${leaf.topCurve.startCtrl.x} ${leaf.topCurve.startCtrl.y} ${leaf.topCurve.endCtrl.x} ${leaf.topCurve.endCtrl.y} ${leaf.topCurve.end.x} ${leaf.topCurve.end.y}`,
      color: '#0f766e',
    },
  ] : [];

  const collarCurveSegments = [
    ...(collar.bottomCurve ? [
      {
        id: 'collar-band-bottom-curve',
        label: 'Collar Band Neck (CbB0→CbF0)',
        d: `M ${collar.bottomCurve.start.x} ${collar.bottomCurve.start.y} C ${collar.bottomCurve.startCtrl.x} ${collar.bottomCurve.startCtrl.y} ${collar.bottomCurve.endCtrl.x} ${collar.bottomCurve.endCtrl.y} ${collar.bottomCurve.end.x} ${collar.bottomCurve.end.y}`,
        color: '#0f766e',
      },
      {
        id: 'collar-band-bottom-curve-layout',
        label: 'Collar Band Neck (CbB0→CbF0)',
        d: `M ${collar.layoutPoints.CbB0.x} ${collar.layoutPoints.CbB0.y} C ${collar.layoutBottomStartCtrl.x} ${collar.layoutBottomStartCtrl.y} ${collar.layoutBottomEndCtrl.x} ${collar.layoutBottomEndCtrl.y} ${collar.layoutPoints.CbF0.x} ${collar.layoutPoints.CbF0.y}`,
        color: '#0f766e',
      },
    ] : []),
    ...(collar.topCurve ? [
      {
        id: 'collar-band-top-curve',
        label: 'Collar Band Top (CbB1→CbE1)',
        d: `M ${collar.topCurve.start.x} ${collar.topCurve.start.y} C ${collar.topCurve.startCtrl.x} ${collar.topCurve.startCtrl.y} ${collar.topCurve.endCtrl.x} ${collar.topCurve.endCtrl.y} ${collar.topCurve.end.x} ${collar.topCurve.end.y}`,
        color: '#0f766e',
      },
      {
        id: 'collar-band-top-curve-layout',
        label: 'Collar Band Top (CbB1→CbE1)',
        d: `M ${collar.layoutPoints.CbB1.x} ${collar.layoutPoints.CbB1.y} C ${collar.layoutTopStartCtrl.x} ${collar.layoutTopStartCtrl.y} ${collar.layoutTopEndCtrl.x} ${collar.layoutTopEndCtrl.y} ${collar.layoutPoints.CbE1.x} ${collar.layoutPoints.CbE1.y}`,
        color: '#0f766e',
      },
    ] : []),
  ];

  const deferred = Array.isArray(body.formulaNarrative?.deferred)
    ? body.formulaNarrative.deferred.filter((item) => item !== 'collar' && item !== 'collar_leaf')
    : [];

  return {
    ...body,
    collarBandPath: collar.path,
    collarBandLayoutPath: collar.layoutPath,
    collarLeafLayoutPath: leaf?.layoutPath || '',
    collarLeafPath: onBodyLeaf?.path || '',
    buttons: [...(body.buttons || []), ...(collar.buttons || [])],
    buttonholes: [...(body.buttonholes || []), ...(collar.buttonholes || [])],
    namedPoints: [...(body.namedPoints || []), ...collarNamedPoints, ...leafNamedPoints],
    curveHandles: [...(body.curveHandles || []), ...collarCurveHandles, ...leafCurveHandles],
    curveSegments: [...(body.curveSegments || []), ...collarCurveSegments, ...leafCurveSegments],
    viewBox: expandDraftViewBox(body.viewBox, [...(collar.draftPoints || []), ...(leaf?.draftPoints || []), ...(onBodyLeaf?.draftPoints || [])]),
    pointReferences: [
      ...(body.pointReferences || []),
      'Collar band V3: CbF0 coincides with CF1. Two shared handles edit the smooth cubic CbB0→CbF0. Default fit walks the front neck to CbT0, then continues to CbB0.',
      'The top follows the current lower curve until a top handle is moved; after that the top stays put so you can shape it independently. Recompute or reset collar handles to resume follow.',
      'Collar leaf (layout): ClB0 defaults to CbB1.x − 0.25"; Free Mode nudge moves ClB0 in X only. Leaf Back Height is the straight CbB1→ClB0 length. ClP0 is half Tie Gap along the band top from CbE1. ClP1 uses Point Length and Spread. ClP0→CbB1 follows the band top.',
      'On-body leaf: ClB0 stays on the CbB0–CbB1 line (plus the layout inset). ClP1 is flipped across the sew-edge tangent at ClP0 so Point Length and the ClP0 corner match layout.',
      'CbF1 is a hidden reference where the stand from CbF0 meets the live band top. The collar button sits at the midpoint of CbF0–CbF1; the hole runs across the stand.',
    ],
    formulaNarrative: {
      ...(body.formulaNarrative || {}),
      deferred,
    },
    matchingSummary: {
      ...(body.matchingSummary || {}),
      ...collar.metrics,
      ...(leaf?.metrics || {}),
    },
    formulaVersion: SHIRT_V3_FORMULA_VERSION,
    geometryVersion: controls.geometryVersion || 'shirt_geom_v3',
  };
}

